import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Button, Space, Tabs, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { Markdown } from '../../../components';
import './report.scss';
import { handleDeerFlowHistory } from '../../../api/grt/ai';

import { processSpecialHeadings, downloadDocxWithStyle, downloadFile, downloadPdf } from '../../hooks/DeerFlow/useReportUtils';

// 导入主题下载图标
import downloadBlue from '../../../assets/images/web/download_blue.webp';
import downloadGreen from '../../../assets/images/web/download_green.webp';
import downloadRed from '../../../assets/images/web/download_red.webp';

// 主题下载图标映射
const downloadIcons = {
    1: downloadBlue,
    2: downloadRed,
    3: downloadGreen
};

// 主题颜色配置
const themeColors = {
    1: '#1677ff', // 蓝色
    2: '#f5222d', // 红色
    3: '#52c41a' // 绿色
};
// 添加全局定时器管理器
let globalPollingTimer = null;
let globalActiveReportId = null;

// 🔥 新增：本地存储管理函数 - 合并后的统一存储键
const UNIFIED_STORAGE_KEY = 'deerflow_reports_data'; // 统一存储键，包含 completed 和 generating 两部分

// 保存完成的报告到本地存储
const saveCompletedReportToLocal = (reportData) => {
    try {
        const { reportId, uniqueReportId, threadId, title, activityContent, reportContent, timestamp } = reportData;
        const storageKey = uniqueReportId || reportId;

        if (!storageKey) {
            console.warn('⚠️ 无效的报告ID，跳过本地存储:', reportData);
            return;
        }

        const unifiedData = JSON.parse(localStorage.getItem(UNIFIED_STORAGE_KEY) || '{"completed":{},"generating":{}}');
        if (!unifiedData.completed) unifiedData.completed = {};
        if (!unifiedData.generating) unifiedData.generating = {};

        const reportToSave = {
            reportId,
            uniqueReportId,
            threadId,
            title: title || '研究报告',
            activityContent: activityContent || '',
            reportContent: reportContent || '',
            timestamp: timestamp || Date.now(),
            isFinal: true,
            savedAt: Date.now()
        };

        unifiedData.completed[storageKey] = reportToSave;
        // 报告完成时，从 generating 中移除
        if (unifiedData.generating[storageKey]) {
            delete unifiedData.generating[storageKey];
        }

        localStorage.setItem(UNIFIED_STORAGE_KEY, JSON.stringify(unifiedData));
    } catch (error) {
        console.error('💾 保存报告到本地存储失败:', error);
    }
};

// 🔥 新增：保存正在生成的步骤状态到本地存储
const saveGeneratingStepsToLocal = (reportId, stepData) => {
    try {
        if (!reportId) {
            console.warn('⚠️ 无效的报告ID，跳过步骤状态存储:', reportId);
            return;
        }

        const unifiedData = JSON.parse(localStorage.getItem(UNIFIED_STORAGE_KEY) || '{"completed":{},"generating":{}}');
        if (!unifiedData.completed) unifiedData.completed = {};
        if (!unifiedData.generating) unifiedData.generating = {};

        unifiedData.generating[reportId] = {
            ...stepData,
            timestamp: Date.now()
        };

        localStorage.setItem(UNIFIED_STORAGE_KEY, JSON.stringify(unifiedData));
    } catch (error) {
        console.error('💾 保存步骤状态到本地存储失败:', error);
    }
};

// 🔥 新增：从本地存储加载正在生成的步骤状态
const loadGeneratingStepsFromLocal = (reportId) => {
    try {
        if (!reportId) {
            return null;
        }

        const unifiedData = JSON.parse(localStorage.getItem(UNIFIED_STORAGE_KEY) || '{"completed":{},"generating":{}}');
        if (!unifiedData.generating) unifiedData.generating = {};
        const stepData = unifiedData.generating[reportId];

        if (stepData) {
            return stepData;
        }

        return null;
    } catch (error) {
        console.error('📖 从本地存储加载步骤状态失败:', error);
        return null;
    }
};

// 🔥 新增：清理正在生成的步骤状态（报告完成时调用）
const clearGeneratingStepsFromLocal = (reportId) => {
    try {
        if (!reportId) {
            return;
        }

        const unifiedData = JSON.parse(localStorage.getItem(UNIFIED_STORAGE_KEY) || '{"completed":{},"generating":{}}');
        if (!unifiedData.generating) unifiedData.generating = {};

        if (unifiedData.generating[reportId]) {
            delete unifiedData.generating[reportId];
            localStorage.setItem(UNIFIED_STORAGE_KEY, JSON.stringify(unifiedData));
        }
    } catch (error) {
        console.error('🧹 清理步骤状态失败:', error);
    }
};

// 从本地存储加载完成的报告
const loadCompletedReportFromLocal = (reportId) => {
    try {
        if (!reportId) {
            console.warn('⚠️ 无效的报告ID，无法从本地存储加载');
            return null;
        }

        const unifiedData = JSON.parse(localStorage.getItem(UNIFIED_STORAGE_KEY) || '{"completed":{},"generating":{}}');
        if (!unifiedData.completed) unifiedData.completed = {};
        const reportData = unifiedData.completed[reportId];

        if (reportData) {
            return reportData;
        }

        return null;
    } catch (error) {
        console.error('📖 从本地存储加载报告失败:', error);
        return null;
    }
};

// 检查报告是否已在本地存储中（包括已完成和正在生成的）
const isReportInLocalStorage = (reportId) => {
    try {
        if (!reportId) return false;
        const unifiedData = JSON.parse(localStorage.getItem(UNIFIED_STORAGE_KEY) || '{"completed":{},"generating":{}}');
        if (!unifiedData.completed) unifiedData.completed = {};
        if (!unifiedData.generating) unifiedData.generating = {};
        // 🔥 修复：检查 completed 和 generating 两部分
        return !!(unifiedData.completed[reportId] || unifiedData.generating[reportId]);
    } catch (error) {
        console.error('检查本地存储失败:', error);
        return false;
    }
};

// 从内容中提取标题的辅助函数
const extractTitleFromContent = (content) => {
    if (!content || typeof content !== 'string') return null;

    // 尝试匹配markdown标题格式
    const titleMatch = content.match(/^#\s+(.+?)$/m);
    if (titleMatch && titleMatch[1] && titleMatch[1].trim() !== '研究报告') {
        return titleMatch[1].trim();
    }

    return null;
};

// 生成下载文件名的辅助函数
const generateDownloadFileName = (title, extension) => {
    // 清理标题，移除不安全的文件名字符
    const cleanTitle = (title || '研究报告')
        .replace(/[\/\\:*?"<>|]/g, '') // 移除文件系统不支持的字符
        .replace(/\s+/g, '_') // 将空格替换为下划线
        .trim();

    // 生成时间戳 (YYYYMMDD_HHMMSS)
    const now = new Date();
    const timestamp =
        [now.getFullYear(), String(now.getMonth() + 1).padStart(2, '0'), String(now.getDate()).padStart(2, '0')].join('') +
        '_' +
        [String(now.getHours()).padStart(2, '0'), String(now.getMinutes()).padStart(2, '0'), String(now.getSeconds()).padStart(2, '0')].join('');

    return `${cleanTitle}_${timestamp}.${extension}`;
};

// 全局定时器清理函数
const clearGlobalTimer = () => {
    if (globalPollingTimer) {
        clearInterval(globalPollingTimer);
        globalPollingTimer = null;
    }
    globalActiveReportId = null;
};

// 全局定时器设置函数
const setGlobalTimer = (reportId, callback, interval = 5000) => {
    // 如果已经有相同reportId的定时器在运行，直接返回
    if (globalActiveReportId === reportId && globalPollingTimer) {
        return;
    }

    // 清理旧的定时器
    clearGlobalTimer();

    // 设置新的定时器
    globalActiveReportId = reportId;
    globalPollingTimer = setInterval(callback, interval);
};

const SimpleReportSidebar = ({
    theme = 1,
    threadId,
    onClose,
    visible,
    uniqueReportId,
    reportId,
    forceRender,
    activityData,
    reportContent,
    data,
    themeColor,
    activeReportCardId, // 添加当前活跃的报告卡片ID
    activeGeneratingReportId // 添加真正正在生成的报告ID
}) => {
    // 基本状态
    const [title, setTitle] = useState('研究报告');
    const [activeTab, setActiveTab] = useState('activities');
    const [hasAutoSwitched, setHasAutoSwitched] = useState(false); // 是否已经自动切换过
    const [userHasSwitched, setUserHasSwitched] = useState(false); // 用户是否手动切换过标签
    const [reportAvailable, setReportAvailable] = useState(false);
    const [contentLoaded, setContentLoaded] = useState(false);
    const [exportBtnHover, setExportBtnHover] = useState(false);
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    // 内容状态
    const [activityMarkdownContent, setActivityMarkdownContent] = useState('');
    const [reportMarkdownContent, setReportMarkdownContent] = useState('');

    // 🔥 新增：基于内容长度变化追踪步骤进展
    const [previousContentLength, setPreviousContentLength] = useState(0);
    const [stepChangeCount, setStepChangeCount] = useState(0);
    const [contentChangeHistory, setContentChangeHistory] = useState([]); // 记录内容变化历史
    const [isRestoringFromCache, setIsRestoringFromCache] = useState(false); // 🔥 新增：标记是否正在从缓存恢复
    const [isGeneratingReport, setIsGeneratingReport] = useState(false); // 🔥 新增：标记是否正在生成报告

    // 下载状态管理 - 使用localStorage保持状态
    const getDownloadStateKey = (type) => `download_${type}_${threadId}`;

    // 🔥 优化：移除不必要的state，使用ref替代避免重绘

    // DOCX下载状态管理
    const [isDownloadingDocx, setIsDownloadingDocx] = useState(false);
    // MD下载状态管理
    const [isDownloadingMd, setIsDownloadingMd] = useState(false);

    // 🔥 新增：防重复提示机制
    const lastWarningTimeRef = useRef(0); // PDF警告时间戳
    const lastDocxWarningTimeRef = useRef(0); // DOCX警告时间戳
    const lastMdWarningTimeRef = useRef(0); // MD警告时间戳

    // 🔥 优化：使用useRef避免状态变化导致的重绘
    const isDownloadingPdfRef = useRef(false);
    const [, forceUpdate] = useState({});

    // 替换原来的状态管理
    const setIsDownloadingPdf = useCallback(
        (downloading) => {
            const wasDownloading = isDownloadingPdfRef.current;
            isDownloadingPdfRef.current = downloading;

            try {
                const key = getDownloadStateKey('pdf');
                if (downloading) {
                    localStorage.setItem(key, 'true');
                } else {
                    localStorage.removeItem(key);
                }
            } catch (error) {
                console.warn('Failed to update localStorage:', error);
            }

            // 只在真正需要时才强制更新UI（比如按钮状态变化）
            if (wasDownloading !== downloading) {
                forceUpdate({});
            }
        },
        [threadId]
    );

    // 获取当前下载状态的函数
    const getIsDownloadingPdf = useCallback(() => {
        return isDownloadingPdfRef.current;
    }, []);

    // 引用
    const pollingTimerRef = useRef(null);
    const isPollingRef = useRef(false);
    // 添加滚动位置管理的引用
    const activitiesScrollRef = useRef(null);
    const reportScrollRef = useRef(null);
    const lastUpdateTimeRef = useRef(0);
    // 添加防抖相关的引用
    const lastPollingTimeRef = useRef(0);
    const isInitialLoadingRef = useRef(false); // 🔥 跟踪初始加载状态
    // 添加已完成任务记录的引用
    const completedReportsRef = useRef(new Set());
    // 添加之前报告ID的引用，用于检测报告切换
    const previousReportIdRef = useRef(null);
    // 🔥 修复：为每个报告建立独立的缓存，避免内容混乱
    const reportCacheRef = useRef({});

    // 主题配置
    const currentThemeColor = themeColors[theme] || themeColors[1];
    const currentDownloadIcon = downloadIcons[theme] || downloadIcons[1];

    // 🔥 修复：报告缓存管理函数
    const getCurrentReportKey = useCallback(() => {
        const currentReportId = uniqueReportId || reportId;
        // 🔥 简化缓存键：只使用threadId和reportId，避免activeReportCardId导致的缓存键不匹配
        const cacheKey = `${threadId}_${currentReportId}`;

        // 🔥 新增：验证缓存键的合理性
        if (!currentReportId || currentReportId === 'undefined' || currentReportId === 'null') {
            console.warn('⚠️ 生成了无效的缓存键:', cacheKey);
        }

        return cacheKey;
    }, [threadId, uniqueReportId, reportId]);

    const getCurrentReportCache = useCallback(() => {
        const key = getCurrentReportKey();

        // 🔥 关键修复：只返回当前报告的实际缓存，如果没有就返回null
        // 这样可以避免自动创建空缓存，确保只显示当前报告的真实数据
        if (!reportCacheRef.current[key]) {
            return null;
        }

        // 验证缓存数据是否属于当前报告
        const cache = reportCacheRef.current[key];
        const hasRealContent = (cache.activityContent && cache.activityContent.length > 0) || (cache.reportContent && cache.reportContent.length > 0);

        if (!hasRealContent) {
            return null;
        }

        return cache;
    }, [getCurrentReportKey]);

    const updateReportCache = useCallback(
        (activityContent, reportContent, isFinal = null) => {
            const key = getCurrentReportKey();

            // 🔥 关键修复：如果缓存不存在，先创建一个新的缓存条目
            if (!reportCacheRef.current[key]) {
                reportCacheRef.current[key] = {
                    activityContent: '',
                    reportContent: '',
                    isFinal: false,
                    timestamp: Date.now(),
                    // 🔥 新增：步骤状态缓存
                    stepChangeCount: 0,
                    contentChangeHistory: [],
                    previousContentLength: 0
                };
            }

            const cache = reportCacheRef.current[key];
            cache.activityContent = activityContent || cache.activityContent;
            cache.reportContent = reportContent || cache.reportContent;
            // 🔥 新增：设置isFinal状态到缓存
            if (isFinal !== null) {
                cache.isFinal = isFinal;
            }
            cache.timestamp = Date.now();
        },
        [getCurrentReportKey]
    );

    const clearCurrentReportCache = () => {
        const key = getCurrentReportKey();
        delete reportCacheRef.current[key];
    };

    // 🔥 新增：更新步骤状态缓存
    const updateStepStateCache = useCallback(
        (stepChangeCount, contentChangeHistory, previousContentLength) => {
            const key = getCurrentReportKey();

            // 确保缓存存在
            if (!reportCacheRef.current[key]) {
                reportCacheRef.current[key] = {
                    activityContent: '',
                    reportContent: '',
                    isFinal: false,
                    timestamp: Date.now(),
                    stepChangeCount: 0,
                    contentChangeHistory: [],
                    previousContentLength: 0
                };
            }

            const cache = reportCacheRef.current[key];
            cache.stepChangeCount = stepChangeCount;
            cache.contentChangeHistory = contentChangeHistory;
            cache.previousContentLength = previousContentLength;
            cache.timestamp = Date.now();

            // 🔥 修复：总是保存步骤状态到本地存储（如果有reportId）
            const currentReportId = uniqueReportId || reportId;
            if (currentReportId) {
                saveGeneratingStepsToLocal(currentReportId, {
                    stepChangeCount,
                    contentChangeHistory,
                    previousContentLength,
                    activityContent: cache.activityContent || '',
                    reportContent: cache.reportContent || ''
                });
            }
        },
        [getCurrentReportKey, isGeneratingReport, uniqueReportId, reportId]
    );

    // 标签页切换处理
    const handleTabChange = (key) => {
        setActiveTab(key);
        // 🔥 修复：用户手动切换标签页时，标记已经进行过自动切换，防止后续自动切换覆盖用户选择
        if (!hasAutoSwitched) {
            setHasAutoSwitched(true);
        }
        // 🔥 新增：标记用户已手动切换过标签，防止后续自动切换
        setUserHasSwitched(true);

        // 🔥 关键修复：切换到报告标签时，检查并同步缓存中的报告内容
        if (key === 'report') {
            const currentCache = getCurrentReportCache();
            if (!reportMarkdownContent && currentCache?.reportContent) {
                setReportMarkdownContent(processSpecialHeadings(currentCache.reportContent));
                setReportAvailable(true);
                setContentLoaded(true);
            }
        }
    };

    // 监听 reportId 变化，重置状态
    useEffect(() => {
        const currentReportId = uniqueReportId || reportId;
        // 🔥 修复闪烁：只有在侧边栏可见且确实切换报告时才清理状态
        const actuallyChanged = previousReportIdRef.current && previousReportIdRef.current !== currentReportId && visible;

        // 🔥 关键修复：即使侧边栏不可见，也要在报告切换时立即更新引用，避免后续混淆
        if (previousReportIdRef.current && previousReportIdRef.current !== currentReportId) {
            previousReportIdRef.current = currentReportId;
        }

        // 🔥 新增：如果是第一次设置，也需要初始化引用
        if (!previousReportIdRef.current && currentReportId) {
            previousReportIdRef.current = currentReportId;
        }

        if (actuallyChanged) {
            // 🔥 修复：切换报告时必须清理旧状态，避免显示混乱
            setActivityMarkdownContent('');
            setReportMarkdownContent('');
            setReportAvailable(false);

            // 🔥 新增：清理下载状态
            setIsDownloadingPdf(false);
            setIsDownloadingDocx(false);

            // 检查新报告是否有缓存内容
            const newReportCache = reportCacheRef.current[`${threadId}_${currentReportId}`];
            const hasReportContent = !!(newReportCache?.reportContent && newReportCache.reportContent.length > 0);
            const isCompletedReport = isReportInLocalStorage(currentReportId);

            // 🔥 智能设置默认标签：如果有报告内容（包括骨架片）或是已完成的报告，默认显示报告标签
            const shouldShowReportTab = hasReportContent || isCompletedReport;
            setActiveTab(shouldShowReportTab ? 'report' : 'activities');

            // 然后立即检查新报告是否有缓存，如果有就恢复
            if (newReportCache?.activityContent || newReportCache?.reportContent) {
                setTimeout(() => {
                    if (newReportCache.activityContent) {
                        setActivityMarkdownContent(processSpecialHeadings(newReportCache.activityContent));
                    }
                    if (newReportCache.reportContent) {
                        setReportMarkdownContent(processSpecialHeadings(newReportCache.reportContent));
                        setReportAvailable(true);
                    }
                    setContentLoaded(true);
                }, 0);
            } else {
                // 🔥 关键修复：新报告没有缓存时，需要清空内容并稳定化标签状态
                setActivityMarkdownContent('');
                setReportMarkdownContent('');
                setReportAvailable(false);
                setContentLoaded(true);
            }

            // 清理已完成报告标记（除非是切换到同一个报告的不同ID形式）
            const newReportKey = `${threadId}_${currentReportId}`;
            const prevReportKey = `${threadId}_${previousReportIdRef.current}`;
            if (newReportKey !== prevReportKey) {
                // 只保留当前报告的完成状态
                const wasCompleted = completedReportsRef.current.has(currentReportId);
                completedReportsRef.current.clear();
                if (wasCompleted) {
                    completedReportsRef.current.add(currentReportId);
                }
            }

            // 这部分逻辑已经移到上面的setTimeout中处理，避免重复
        }

        previousReportIdRef.current = currentReportId;
    }, [reportId, uniqueReportId, threadId, visible]);

    // 优化的内容更新函数
    const updateContentSafely = useCallback(
        (newActivityContent, newReportContent, extractedTitle, isFromCache = false, overrideIsFinal = null) => {
            // 🔥 新增：检查是否为当前报告的内容，防止跨报告内容混淆
            const currentReportKey = uniqueReportId || reportId;

            // 🔥 关键修复：对于新报告（没有内容），如果传入的是实质性内容，需要验证内容来源
            // 防止新报告错误地显示其他报告的内容
            if (!isFromCache && (newActivityContent?.length > 100 || newReportContent?.length > 100)) {
                const currentCache = getCurrentReportCache();
                const hasCurrentReportContent = currentCache && (currentCache.activityContent || currentCache.reportContent);

                // 如果当前报告没有缓存内容，但传入了大量内容，可能是错误的内容
                if (!hasCurrentReportContent) {
                    // 如果这是从其他报告切换过来的，且内容量很大，需要谨慎处理
                    if (previousReportIdRef.current && previousReportIdRef.current !== currentReportKey) {
                        return;
                    }
                }
            }

            // 🔥 修复：只在确实不匹配且有实质内容时才拦截，避免阻止正常的内容更新
            if (
                !isFromCache &&
                previousReportIdRef.current &&
                previousReportIdRef.current !== currentReportKey &&
                (newActivityContent || newReportContent)
            ) {
                return;
            }

            // 🔥 添加调用堆栈追踪，帮助诊断死循环
            const callStack = new Error().stack?.split('\n').slice(2, 5).join(' → ') || 'unknown';

            // 🔥 修复闪烁：增强防抖和内容重复检查
            const now = Date.now();
            const timeSinceLastUpdate = lastUpdateTimeRef.current ? now - lastUpdateTimeRef.current : 1000;

            // 🔥 关键：防抖 + 内容重复检查（只在频繁更新时防抖）
            if (timeSinceLastUpdate < 300 && !isFromCache) {
                return;
            }

            // 🔥 内容重复检查：如果内容完全相同，跳过更新（但允许从缓存恢复）
            const currentActivityLength = activityMarkdownContent?.length || 0;
            const currentReportLength = reportMarkdownContent?.length || 0;

            if (
                currentActivityLength === newActivityContent.length &&
                currentReportLength === newReportContent.length &&
                currentActivityLength > 0 &&
                !isFromCache
            ) {
                return;
            }

            lastUpdateTimeRef.current = now;

            // 🛡️ 强化历史报告保护：检查当前是否在查看历史报告
            const currentReportId = uniqueReportId || reportId;
            const isCurrentActiveReport =
                !activeReportCardId ||
                currentReportId === activeReportCardId ||
                reportId === activeReportCardId ||
                uniqueReportId === activeReportCardId;

            // 🔥 修复无法打开：只在确实需要保护时才拦截
            // 允许正常的报告切换和内容加载
            const shouldProtect =
                !isCurrentActiveReport &&
                !isFromCache &&
                (activityMarkdownContent || reportMarkdownContent) &&
                activeReportCardId &&
                currentReportId !== activeReportCardId;

            if (shouldProtect) {
                return;
            }

            // 🔥 新增：防止空内容覆盖已有内容（除非是明确的清空操作）
            const hasExistingContent =
                (activityMarkdownContent && activityMarkdownContent.length > 50) || (reportMarkdownContent && reportMarkdownContent.length > 50);
            const newContentIsEmpty =
                (!newActivityContent || newActivityContent.length === 0) && (!newReportContent || newReportContent.length === 0);

            if (hasExistingContent && newContentIsEmpty && !isFromCache) {
                return;
            }

            // 对于已完成报告的额外保护（保留原有逻辑作为备份）
            if (data?.isFinal && (activityMarkdownContent || reportMarkdownContent)) {
                const hasSubstantialCurrentContent =
                    (activityMarkdownContent && activityMarkdownContent.length > 100) ||
                    (reportMarkdownContent && reportMarkdownContent.length > 100);
                const newContentShorter =
                    (newActivityContent && newActivityContent.length < (activityMarkdownContent?.length || 0) * 0.5) ||
                    (newReportContent && newReportContent.length < (reportMarkdownContent?.length || 0) * 0.5);

                if (hasSubstantialCurrentContent && newContentShorter) {
                    return;
                }
            }

            // 🔥 修复：使用当前报告的缓存检查内容变化
            const currentCache = getCurrentReportCache();
            const activityChanged = currentCache ? newActivityContent !== currentCache.activityContent : !!newActivityContent;
            const reportChanged = currentCache ? newReportContent !== currentCache.reportContent : !!newReportContent;

            // 🔥 修复：检查是否需要更新isFinal状态
            const needsIsFinalUpdate = overrideIsFinal !== null && overrideIsFinal !== data?.isFinal;

            if (!activityChanged && !reportChanged && !needsIsFinalUpdate) {
                return;
            }

            // 如果只是状态更新（内容未变但isFinal需要更新）
            if (!activityChanged && !reportChanged && needsIsFinalUpdate) {
                // 更新缓存中的isFinal状态
                const currentCache = getCurrentReportCache();
                if (!currentCache) {
                    return;
                }
                const newCacheData = {
                    ...currentCache,
                    isFinal: overrideIsFinal,
                    timestamp: Date.now()
                };
                updateReportCache(newCacheData.activityContent, newCacheData.reportContent, overrideIsFinal);

                // 🔥 新增：如果状态变为完成，保存到本地存储
                if (overrideIsFinal && (newCacheData.activityContent || newCacheData.reportContent)) {
                    const currentReportId = uniqueReportId || reportId;
                    const finalTitle = extractedTitle || extractTitleFromContent(newCacheData.reportContent) || data?.title || title || '研究报告';

                    const reportDataToSave = {
                        reportId: currentReportId,
                        uniqueReportId: currentReportId,
                        threadId,
                        title: finalTitle,
                        activityContent: newCacheData.activityContent || '',
                        reportContent: newCacheData.reportContent || '',
                        timestamp: Date.now()
                    };

                    saveCompletedReportToLocal(reportDataToSave);
                }

                // 派发状态更新事件
                const reportStateEvent = new CustomEvent('report-content-update', {
                    detail: {
                        threadId,
                        reportId: uniqueReportId || reportId,
                        uniqueReportId: uniqueReportId || reportId,
                        activityData: newCacheData.activityContent,
                        reportData: newCacheData.reportContent,
                        title: extractedTitle || data?.title || '研究报告',
                        isFinal: overrideIsFinal,
                        isNew: false,
                        clearPrevious: false,
                        timestamp: Date.now()
                    }
                });
                window.dispatchEvent(reportStateEvent);
                return;
            }

            // 保存当前滚动位置
            if (activeTab === 'activities' && activityChanged) {
                saveScrollPosition('activities');
            }
            if (activeTab === 'report' && reportChanged) {
                saveScrollPosition('report');
            }

            // 🔥 修复：更新当前报告的缓存
            if (activityChanged || reportChanged) {
                // 🔥 优先使用 overrideIsFinal，如果没有则使用 data?.isFinal
                const finalStatus = overrideIsFinal !== null ? overrideIsFinal : data?.isFinal;
                const finalActivityContent = activityChanged ? newActivityContent : currentCache?.activityContent || '';
                const finalReportContent = reportChanged ? newReportContent : currentCache?.reportContent || '';

                updateReportCache(
                    finalActivityContent,
                    finalReportContent,
                    finalStatus // 🔥 使用正确的isFinal状态
                );

                // 🔥 新增：如果报告已完成，保存到本地存储
                if (finalStatus && (finalActivityContent || finalReportContent)) {
                    const currentReportId = uniqueReportId || reportId;
                    const finalTitle = extractedTitle || extractTitleFromContent(finalReportContent) || title || '研究报告';

                    const reportDataToSave = {
                        reportId: currentReportId,
                        uniqueReportId: currentReportId,
                        threadId,
                        title: finalTitle,
                        activityContent: finalActivityContent,
                        reportContent: finalReportContent,
                        timestamp: Date.now()
                    };

                    saveCompletedReportToLocal(reportDataToSave);
                }
            }

            // 批量更新状态
            const updates = {};

            if (activityChanged) {
                updates.activityMarkdownContent = processSpecialHeadings(newActivityContent);
            }
            if (reportChanged) {
                updates.reportMarkdownContent = processSpecialHeadings(newReportContent);
            }

            // 🔥 关键修复：无论内容是否变化，都要检查并设置reportAvailable状态
            // 这确保当报告完成时，即使内容没有变化，标签页也能正确启用
            if (newReportContent || reportChanged) {
                updates.reportAvailable = !!newReportContent;
            } else if (!reportChanged && getCurrentReportCache()?.reportContent) {
                // 如果内容没有变化但缓存中有报告内容，也要确保reportAvailable为true
                updates.reportAvailable = true;
            }

            if (extractedTitle && extractedTitle !== title) {
                updates.title = extractedTitle;
            }
            updates.contentLoaded = true;

            // 使用 setTimeout 来批量更新状态，避免频繁渲染
            setTimeout(() => {
                if (updates.activityMarkdownContent !== undefined) {
                    setActivityMarkdownContent(updates.activityMarkdownContent);
                }
                if (updates.reportMarkdownContent !== undefined) {
                    setReportMarkdownContent(updates.reportMarkdownContent);
                }
                if (updates.reportAvailable !== undefined) {
                    setReportAvailable(updates.reportAvailable);
                }
                if (updates.title !== undefined) {
                    setTitle(updates.title);
                }
                if (updates.contentLoaded !== undefined) {
                    setContentLoaded(updates.contentLoaded);
                }

                // 如果有报告内容且还没有自动切换过，自动切换到报告标签
                // 但如果是从缓存恢复的内容，不自动切换
                // 🔥 修复：添加userHasSwitched检查，避免在用户手动切换后再次自动切换
                // 🔥 新增：智能识别是否为真正的报告内容，而不是研究员的思考过程
                const isActualReport =
                    newReportContent &&
                    // 检查内容特征：真正的报告通常更结构化
                    (newReportContent.includes('# ') || // 包含一级标题
                        newReportContent.includes('## ') || // 包含二级标题
                        newReportContent.length > 500 || // 内容足够长
                        // 检查是否包含报告特有的结构关键词
                        /总结|结论|建议|摘要|概述|分析结果/.test(newReportContent));

                if (isActualReport && activeTab === 'activities' && !hasAutoSwitched && !isFromCache && !userHasSwitched) {
                    setActiveTab('report');
                    setHasAutoSwitched(true);
                }

                // 在下一个渲染周期恢复滚动位置
                setTimeout(() => {
                    if (activeTab === 'activities' && activityChanged) {
                        restoreScrollPosition('activities');
                    }
                    if (activeTab === 'report' && reportChanged) {
                        restoreScrollPosition('report');
                    }
                }, 0);
            }, 0);
        },
        [
            uniqueReportId,
            reportId,
            data?.isFinal,
            activityMarkdownContent,
            reportMarkdownContent,
            getCurrentReportCache,
            updateReportCache,
            getCurrentReportKey,
            title,
            activeTab,
            hasAutoSwitched
        ]
    );

    // 从历史记录加载报告数据
    const loadReportFromHistory = useCallback(async () => {
        const currentReportId = uniqueReportId || reportId;

        // 🔥 防重复调用：检查是否有相同请求正在进行中
        const requestKey = `${currentReportId}_${threadId}`;
        if (window.pendingHistoryRequests && window.pendingHistoryRequests.has(requestKey)) {
            return;
        }

        // 标记请求开始
        if (!window.pendingHistoryRequests) {
            window.pendingHistoryRequests = new Set();
        }
        window.pendingHistoryRequests.add(requestKey);

        // 如果没有threadId但有reportId，尝试从本地存储获取threadId
        let effectiveThreadId = threadId;
        if (!effectiveThreadId && currentReportId) {
            try {
                const unifiedData = JSON.parse(localStorage.getItem('deerflow_reports_data') || '{"completed":{},"generating":{}}');
                const completedReports = unifiedData.completed || {};
                const generatingReports = unifiedData.generating || {};
                const reportData = completedReports[currentReportId] || generatingReports[currentReportId];
                if (reportData && reportData.threadId) {
                    effectiveThreadId = reportData.threadId;
                }
            } catch (error) {
                console.error('从本地存储获取threadId失败:', error);
            }
        }

        if (!effectiveThreadId) {
            console.warn('无法获取threadId，跳过历史报告加载:', { threadId, currentReportId });
            // 清理请求标记
            if (window.pendingHistoryRequests) {
                window.pendingHistoryRequests.delete(requestKey);
            }
            return;
        }

        // 🔥 修复闪烁：避免在活跃报告生成时重复加载
        // 只有当前查看的是正在生成的活跃报告时才跳过
        const isViewingGeneratingReport = currentReportId === activeGeneratingReportId && !data?.isFinal;
        if (isViewingGeneratingReport) {
            // 清理请求标记
            if (window.pendingHistoryRequests) {
                window.pendingHistoryRequests.delete(requestKey);
            }
            return;
        }

        // 轮询开始前的保护检查
        if (
            currentReportId &&
            (completedReportsRef.current.has(currentReportId) ||
                completedReportsRef.current.has(reportId) ||
                completedReportsRef.current.has(uniqueReportId))
        ) {
            // 清理请求标记
            if (window.pendingHistoryRequests) {
                window.pendingHistoryRequests.delete(requestKey);
            }
            return;
        }

        // 额外保护：如果当前是已完成的历史报告且有实质性内容，不执行轮询更新
        if (
            data?.isFinal &&
            ((activityMarkdownContent && activityMarkdownContent.length > 100) || (reportMarkdownContent && reportMarkdownContent.length > 100))
        ) {
            // 清理请求标记
            if (window.pendingHistoryRequests) {
                window.pendingHistoryRequests.delete(requestKey);
            }
            return;
        }

        // 🔥 移除重复的缓存恢复逻辑，已在visible变化时处理

        // 获取当前报告缓存（供下面多处使用）
        const currentCache = getCurrentReportCache();

        // 检查报告是否已完成，但如果没有任何内容则允许加载一次
        if (currentReportId && completedReportsRef.current.has(currentReportId)) {
            // 如果已完成的报告没有任何内容（包括缓存），允许加载一次
            const hasAnyContent =
                activityMarkdownContent || reportMarkdownContent || (currentCache && (currentCache.activityContent || currentCache.reportContent));

            if (hasAnyContent) {
                return;
            } else {
                // 继续执行API调用
            }
        }

        // 防抖机制：如果上次调用时间太近，则跳过（但允许手动操作）
        const now = Date.now();
        const timeSinceLastCall = now - lastPollingTimeRef.current;
        const isManualCall = !isPollingRef.current; // 如果不在轮询中，说明是手动调用

        if (timeSinceLastCall < (isManualCall ? 1000 : 5000)) {
            return;
        }
        lastPollingTimeRef.current = now;

        try {
            // 如果是轮询且当前没有内容，显示骨架屏
            // 但要检查引用中是否有缓存的内容，避免清空已有内容
            if (
                !activityMarkdownContent &&
                !reportMarkdownContent &&
                !(currentCache && (currentCache.activityContent || currentCache.reportContent))
            ) {
                setContentLoaded(false);
            }

            const response = await handleDeerFlowHistory({ thread_id: effectiveThreadId });

            // 🔥 调试：详细记录任务状态
            const taskStatus = response.data.status;

            if (response && response.code === 200 && response.data && response.data.messages) {
                const messages = response.data.messages;
                const taskStatus = response.data.status; // 获取任务状态

                // 🔥 新增：获取本地存储中保存的选中报告ID（对象形式，支持多个报告）
                const selectedReportId = (() => {
                    try {
                        if (!threadId) return null;
                        const selectedReports = JSON.parse(localStorage.getItem('selected_report_id') || '{}');
                        return selectedReports[threadId] || null;
                    } catch (error) {
                        console.warn('获取选中报告ID失败:', error);
                        return null;
                    }
                })();

                // 🔥 重要：根据选中的报告ID过滤消息，确保数据精确匹配
                let filteredMessages = messages;
                if (selectedReportId) {
                    // 使用选中的报告ID过滤消息
                    filteredMessages = messages.filter((msg) => {
                        // 优先匹配task_id，这是最可靠的标识符
                        if (msg.task_id === selectedReportId) {
                            return true;
                        }
                        // 如果消息有skill_id，检查是否匹配
                        if (msg.skill_id === selectedReportId) {
                            return true;
                        }
                        // 对于没有task_id的消息（如旧格式），暂时保留
                        return !msg.task_id;
                    });

                    // 🔥 如果过滤后没有匹配的消息，回退到原始逻辑
                    if (filteredMessages.length === 0) {
                        filteredMessages = messages;
                    }
                } else {
                }

                // 查找包含报告数据的消息
                let activityContent = '';
                let reportContent = '';
                let reportTitle = '研究报告';

                // 找到与当前reportId对应的任务
                let currentTaskId = null;
                let currentTaskStartIndex = -1;
                let hasUserMessage = false; // 标记是否有用户消息

                // 方法0：精确匹配 - 根据传入的reportId查找对应的任务
                // 只对已完成的历史报告（isFinal: true）使用精确匹配
                // 对正在生成的新报告（isFinal: false）跳过精确匹配，让系统使用最新任务逻辑
                if (reportId && uniqueReportId && data?.isFinal) {
                    // 首先尝试直接匹配task_id
                    for (let i = 0; i < filteredMessages.length; i++) {
                        const msg = filteredMessages[i];
                        if (msg.task_id === reportId || msg.task_id === uniqueReportId || msg.task_id === selectedReportId) {
                            // 找到匹配的消息，现在查找这个任务的开始位置
                            if ((msg.name === 'task_begin' || msg.role === 'task_begin') && msg.content) {
                                currentTaskId = msg.task_id;
                                currentTaskStartIndex = i;
                                reportTitle = msg.content;
                                break;
                            } else if (currentTaskId === null) {
                                // 记录找到的task_id，但继续寻找task_begin
                                currentTaskId = msg.task_id;
                                // 如果没有task_begin，从第一个匹配的消息开始
                                if (currentTaskStartIndex === -1) {
                                    currentTaskStartIndex = i;
                                }
                            }
                        }
                    }

                    // 如果精确匹配成功，跳过其他查找方法
                    if (currentTaskId && currentTaskStartIndex >= 0) {
                    }
                } else if (reportId && uniqueReportId) {
                    // 对于历史报告，即使isFinal为false，也应该尝试精确匹配
                    // 只有当前活跃报告卡片与当前查看的报告ID一致时，才跳过精确匹配
                    const isCurrentActiveReport = activeReportCardId === (uniqueReportId || reportId);
                    if (!data?.isFinal && isCurrentActiveReport) {
                    } else {
                    }
                }

                // 方法1：优先查找最新的task_begin消息（用于新开始的研究）
                // 只有在精确匹配失败且是当前活跃报告时才使用
                const isCurrentActiveReport = activeReportCardId === (uniqueReportId || reportId);

                // 🔥 重要修复：对于历史报告，即使data.isFinal为false，也不应该使用最新任务逻辑
                // 只有当前真正在生成中的报告才应该使用最新任务逻辑
                const shouldUseLatestTask = currentTaskStartIndex === -1 && !data?.isFinal && isCurrentActiveReport;
                if (shouldUseLatestTask) {
                    for (let i = filteredMessages.length - 1; i >= 0; i--) {
                        const msg = filteredMessages[i];
                        if ((msg.name === 'task_begin' || msg.role === 'task_begin') && msg.content) {
                            currentTaskId = msg.task_id;
                            currentTaskStartIndex = i;
                            reportTitle = msg.content;
                            break;
                        }
                    }
                }

                // 方法2：如果没有task_begin，查找最新的用户消息来确定task_id
                if (shouldUseLatestTask && currentTaskStartIndex === -1) {
                    for (let i = filteredMessages.length - 1; i >= 0; i--) {
                        const msg = filteredMessages[i];
                        if (msg.name === 'human' && msg.task_id) {
                            currentTaskId = msg.task_id;
                            currentTaskStartIndex = i;
                            hasUserMessage = true;
                            // 从用户消息开始，但标题保持默认
                            break;
                        }
                    }
                }

                // 方法3：如果当前没有找到对应的task_id，查找最新的任何task_id
                // 这种情况通常发生在点击"开始研究"后
                if (shouldUseLatestTask && currentTaskStartIndex === -1) {
                    for (let i = filteredMessages.length - 1; i >= 0; i--) {
                        const msg = filteredMessages[i];
                        if (msg.task_id) {
                            currentTaskId = msg.task_id;
                            currentTaskStartIndex = i;
                            break;
                        }
                    }
                }

                // 🔥 修复：如果找到了task_id但与当前reportId不同，可能需要更新映射
                if (currentTaskId && currentTaskId !== reportId) {
                }

                // 如果找到了任务开始，只处理该任务相关的消息
                if (currentTaskStartIndex >= 0 && currentTaskId) {
                    // 🔥 修复：简化任务ID匹配逻辑，不再区分临时ID和真实ID
                    const expectedTaskId = uniqueReportId || reportId;

                    // 🔥 修复无法打开：放宽历史报告保护条件
                    // 只有在确实是不同报告且当前有内容时才保护，避免阻止正常打开
                    const hasCurrentContent = !!(activityMarkdownContent || reportMarkdownContent);
                    if (!isCurrentActiveReport && currentTaskId !== expectedTaskId && hasCurrentContent) {
                        return;
                    }

                    // 🔥 修复：新报告状态保护：确保新报告不会被其他任务数据污染
                    const isCompletedReport = taskStatus === -1 || taskStatus === 1; // 🔥 修复：status=1时也视为完成
                    if (isCurrentActiveReport && !isCompletedReport && currentTaskId !== expectedTaskId) {
                        // 清理可能的污染数据
                        setActivityMarkdownContent('');
                        setReportMarkdownContent('');
                        return;
                    }
                    // 从任务开始位置往后处理消息
                    for (let i = currentTaskStartIndex; i < filteredMessages.length; i++) {
                        const msg = filteredMessages[i];
                        const messageType = msg.name || msg.role;

                        // 只处理属于当前任务的消息
                        if (msg.task_id === currentTaskId) {
                            if ((messageType === 'researcher' || messageType === 'coder') && msg.content) {
                                activityContent += msg.content + '\n\n';
                            } else if (messageType === 'reporter' && msg.content) {
                                reportContent = msg.content;
                            }
                        }
                    }
                } else {
                    // 如果没有找到task_begin，使用原来的逻辑（向后兼容）
                    filteredMessages.forEach((msg) => {
                        const messageType = msg.name || msg.role;

                        if ((messageType === 'researcher' || messageType === 'coder') && msg.content) {
                            activityContent += msg.content + '\n\n';
                        } else if (messageType === 'reporter' && msg.content) {
                            reportContent = msg.content;
                        } else if (messageType === 'task_begin' && msg.content) {
                            reportTitle = msg.content;
                        }
                    });
                }

                // 清理内容
                activityContent = activityContent.trim();
                reportContent = reportContent.trim();

                // 提取标题
                let extractedTitle = reportTitle;
                if (reportContent) {
                    const titleMatch = reportContent.match(/^#\s+(.+?)$/m);
                    if (titleMatch && titleMatch[1]) {
                        extractedTitle = titleMatch[1];
                    }
                }

                // 检查任务是否已完成：必须满足以下条件之一
                // 1. status状态明确为1（后端确认完成）
                // 2. 存在name为"reporter"且task_id匹配的消息（表示最终报告已生成）
                const hasReporterMessage = filteredMessages.some(
                    (msg) => msg.name === 'reporter' && msg.role === 'assistant' && msg.task_id === currentTaskId
                );

                // 🔥 修正完成判断逻辑：综合考虑多种完成条件
                // 1. 任务状态为-1表示已完成
                // 2. 🔥 关键修复：任务状态为1时也表示已完成，必须停止轮询
                // 3. 如果有完整的reporter消息且内容充实，也认为任务完成
                // 4. 🔥 新增：如果报告内容很长且格式完整，强制视为完成
                const hasCompleteReporterMessage = hasReporterMessage && reportContent && reportContent.length > 500;
                const hasVeryCompleteContent =
                    reportContent && reportContent.length > 1500 && reportContent.includes('# ') && reportContent.includes('## ');
                const isTaskCompleted =
                    taskStatus === -1 ||
                    taskStatus === 1 || // 🔥 关键修复：status=1时直接认为完成
                    hasVeryCompleteContent;

                // 更严格的判断：只有真正完成时才停止轮询
                const shouldConsiderComplete = isTaskCompleted;

                const currentReportId = uniqueReportId || reportId;

                // 使用优化的内容更新函数
                if (activityContent || reportContent) {
                    // 检查是否是当前显示的报告，避免不同报告的数据相互覆盖
                    const isCurrentActiveReport =
                        !activeReportCardId ||
                        currentReportId === activeReportCardId ||
                        reportId === activeReportCardId ||
                        uniqueReportId === activeReportCardId;

                    if (!isCurrentActiveReport) {
                        return;
                    }
                    updateContentSafely(activityContent, reportContent, extractedTitle, false, isTaskCompleted);

                    // 通知父组件更新reportDataRef
                    const updateReportDataEvent = new CustomEvent('update-report-data', {
                        detail: {
                            threadId: effectiveThreadId,
                            reportId: currentReportId,
                            uniqueReportId: currentReportId,
                            activityData: activityContent,
                            reportContent: reportContent,
                            title: extractedTitle,
                            isFinal: isTaskCompleted // 基于实际任务状态设置
                        }
                    });
                    window.dispatchEvent(updateReportDataEvent);
                }

                // 只有在明确完成时才停止轮询
                if (shouldConsiderComplete) {
                    // 记录该报告已完成，避免重复轮询
                    if (currentReportId) {
                        completedReportsRef.current.add(currentReportId);
                    }

                    // 🔥 新增：保存完成的报告到本地存储
                    if (activityContent || reportContent) {
                        const reportDataToSave = {
                            reportId: currentReportId,
                            uniqueReportId: currentReportId,
                            threadId: effectiveThreadId,
                            title: extractedTitle || '研究报告',
                            activityContent: activityContent || '',
                            reportContent: reportContent || '',
                            timestamp: Date.now()
                        };

                        saveCompletedReportToLocal(reportDataToSave);
                    }

                    // 🔥 新增：报告完成时清理正在生成的步骤状态
                    clearGeneratingStepsFromLocal(currentReportId);
                    setIsGeneratingReport(false);

                    // 停止轮询，因为报告已完成
                    stopPolling();

                    // 🔥 关键修复：通知父组件同步更新deerFlowManager的completedReportsRef
                    const syncManagerEvent = new CustomEvent('sync-deerflow-manager', {
                        detail: {
                            action: 'markCompleted',
                            reportId: currentReportId,
                            uniqueReportId: currentReportId,
                            reason: 'SimpleReportSidebar检测到任务完成'
                        }
                    });
                    window.dispatchEvent(syncManagerEvent);

                    // 通知父组件更新状态
                    if (reportContent) {
                        const reportCompleteEvent = new CustomEvent('report-complete-from-history', {
                            detail: {
                                threadId: effectiveThreadId,
                                reportId: uniqueReportId || reportId,
                                uniqueReportId: uniqueReportId || reportId,
                                title: extractedTitle,
                                isFinal: true,
                                status: 'success'
                            }
                        });
                        window.dispatchEvent(reportCompleteEvent);
                    }
                }
            }
        } catch (error) {
            console.error('从历史记录获取报告数据失败:', error);
        } finally {
            // 清理请求标记
            const requestKey = `${currentReportId}_${threadId}`;
            if (window.pendingHistoryRequests) {
                window.pendingHistoryRequests.delete(requestKey);
            }
        }
    }, [uniqueReportId, reportId, threadId, updateContentSafely]);

    // 监听 visible 变化，重置自动切换状态
    useEffect(() => {
        if (visible) {
            // 检查是否是历史报告（已完成的报告）
            const visibleReportId = uniqueReportId || reportId;

            // 🔥 关键修复：检测报告切换（包括首次打开新报告的情况）
            const isReportChanged = previousReportIdRef.current !== visibleReportId;

            // 🔥 修复：只在报告切换时重置用户状态，避免内容更新时误重置
            if (isReportChanged) {
                setHasAutoSwitched(false); // 每次切换报告时重置自动切换标志
                setUserHasSwitched(false); // 重置用户手动切换标志
            }

            // 🔥 新增：检测并清空不匹配的内容
            // 如果报告ID发生变化且当前有显示内容，立即清空
            if (isReportChanged && (activityMarkdownContent || reportMarkdownContent)) {
                setActivityMarkdownContent('');
                setReportMarkdownContent('');
                setReportAvailable(false);
                setContentLoaded(false);

                // 🎯 修复：检查是否是完成的报告，决定默认标签页
                const isCompletedReport = data?.isFinal || isReportInLocalStorage(visibleReportId);
                setActiveTab(isCompletedReport ? 'report' : 'activities');
                setHasAutoSwitched(false);
                setUserHasSwitched(false);
            }

            // 🔥 关键修复：立即更新previousReportIdRef，确保后续缓存操作使用正确的reportId
            if (isReportChanged) {
                previousReportIdRef.current = visibleReportId;

                // 🔥 额外保护：强制清空所有可能的残留状态，防止旧内容显示
                if (activityMarkdownContent || reportMarkdownContent) {
                    setActivityMarkdownContent('');
                    setReportMarkdownContent('');
                    setReportAvailable(false);
                    setContentLoaded(false);
                }
            }

            // 🔥 修复闪烁：防止重复数据加载的优化逻辑
            const currentCache = getCurrentReportCache();
            const hasDisplayedContent = !!(activityMarkdownContent || reportMarkdownContent);
            const hasCachedContent = currentCache && !!(currentCache.activityContent || currentCache.reportContent);

            // 🔥 关键修复：避免在有活跃报告生成时重复加载
            const isActiveReportGenerating = activeReportCardId === visibleReportId && !data?.isFinal;

            // 🔥 修复报告切换bug：检查缓存是否为当前报告且有实际内容
            const currentReportKey = getCurrentReportKey(); // 使用统一的缓存键生成函数
            const hasValidCache =
                reportCacheRef.current[currentReportKey] &&
                (reportCacheRef.current[currentReportKey].activityContent || reportCacheRef.current[currentReportKey].reportContent);

            // 🔥 新增：优先从本地存储加载完成的报告
            const currentActiveReportId = uniqueReportId || reportId;

            // 如果是历史报告且本地存储中有数据，优先从本地存储加载
            if (!hasDisplayedContent && currentActiveReportId && isReportInLocalStorage(currentActiveReportId)) {
                const localReportData = loadCompletedReportFromLocal(currentActiveReportId);

                if (localReportData && (localReportData.activityContent || localReportData.reportContent)) {
                    // 更新状态显示本地数据
                    setActivityMarkdownContent(processSpecialHeadings(localReportData.activityContent || ''));
                    setReportMarkdownContent(processSpecialHeadings(localReportData.reportContent || ''));
                    setTitle(localReportData.title || '研究报告');
                    setReportAvailable(!!localReportData.reportContent);
                    setContentLoaded(true);

                    // 🔥 新增：对于完成的报告，设置步骤状态为完成状态（4步）
                    if (localReportData.activityContent) {
                        setStepChangeCount(4); // 🔥 修改：改为4步
                        setContentChangeHistory([
                            { timestamp: Date.now() - 4000, length: Math.floor(localReportData.activityContent.length / 4) },
                            { timestamp: Date.now() - 3000, length: Math.floor(localReportData.activityContent.length / 2) },
                            { timestamp: Date.now() - 2000, length: Math.floor((localReportData.activityContent.length * 3) / 4) },
                            { timestamp: Date.now() - 1000, length: localReportData.activityContent.length }
                        ]);
                        setPreviousContentLength(localReportData.activityContent.length);

                        // 同时更新缓存
                        updateStepStateCache(
                            4,
                            [
                                // 🔥 修改：改为4步
                                { timestamp: Date.now() - 4000, length: Math.floor(localReportData.activityContent.length / 4) },
                                { timestamp: Date.now() - 3000, length: Math.floor(localReportData.activityContent.length / 2) },
                                { timestamp: Date.now() - 2000, length: Math.floor((localReportData.activityContent.length * 3) / 4) },
                                { timestamp: Date.now() - 1000, length: localReportData.activityContent.length }
                            ],
                            localReportData.activityContent.length
                        );
                    }

                    // 🎯 完成的报告默认展示报告标签页
                    if (localReportData.reportContent && !hasAutoSwitched && !userHasSwitched) {
                        setActiveTab('report');
                        setHasAutoSwitched(true);
                    }

                    // 同时更新缓存，确保数据一致性
                    updateReportCache(
                        localReportData.activityContent || '',
                        localReportData.reportContent || '',
                        true // 标记为最终版本
                    );

                    // 标记报告已完成
                    completedReportsRef.current.add(currentActiveReportId);

                    // 🔥 修复：设置previousReportIdRef，避免被后续useEffect重置
                    previousReportIdRef.current = currentActiveReportId;

                    // 触发完成事件确保按钮状态正确
                    setTimeout(() => {
                        const reportCompleteEvent = new CustomEvent('report-complete-from-history', {
                            detail: {
                                threadId: localReportData.threadId,
                                reportId: currentActiveReportId,
                                uniqueReportId: currentActiveReportId,
                                title: localReportData.title,
                                isFinal: true,
                                status: 'success'
                            }
                        });
                        window.dispatchEvent(reportCompleteEvent);
                    }, 50);

                    return; // 已从本地存储加载，跳过其他加载逻辑
                }
            }

            // 🔥 新增：检查是否有正在生成的步骤状态需要恢复
            if (!hasDisplayedContent && currentActiveReportId) {
                const generatingStepsData = loadGeneratingStepsFromLocal(currentActiveReportId);

                if (generatingStepsData) {
                    // 🔥 重要：设置恢复标志，防止内容变化检测误触发
                    setIsRestoringFromCache(true);

                    // 恢复步骤状态
                    setStepChangeCount(generatingStepsData.stepChangeCount || 0);
                    setContentChangeHistory(generatingStepsData.contentChangeHistory || []);
                    setPreviousContentLength(generatingStepsData.previousContentLength || 0);

                    // 恢复内容
                    if (generatingStepsData.activityContent) {
                        setActivityMarkdownContent(processSpecialHeadings(generatingStepsData.activityContent));
                    }
                    if (generatingStepsData.reportContent) {
                        setReportMarkdownContent(processSpecialHeadings(generatingStepsData.reportContent));
                        setReportAvailable(true);
                    }

                    setContentLoaded(true);
                    setIsGeneratingReport(true); // 标记为正在生成状态

                    // 🔥 新增：智能设置默认标签页 - 如果有报告内容或思考过程完成，默认显示报告标签
                    const maxSteps = 3;
                    const hasReportContent = !!(generatingStepsData.reportContent && generatingStepsData.reportContent.trim().length > 0);
                    const isThinkingComplete = (generatingStepsData.stepChangeCount || 0) >= maxSteps;
                    const shouldShowReportSkeleton = isThinkingComplete && !hasReportContent;

                    if ((hasReportContent || shouldShowReportSkeleton) && !hasAutoSwitched && !userHasSwitched) {
                        setActiveTab('report');
                        setHasAutoSwitched(true);
                    }

                    // 更新缓存
                    updateReportCache(
                        generatingStepsData.activityContent || '',
                        generatingStepsData.reportContent || '',
                        false // 标记为未完成
                    );

                    // 更新步骤状态缓存
                    updateStepStateCache(
                        generatingStepsData.stepChangeCount || 0,
                        generatingStepsData.contentChangeHistory || [],
                        generatingStepsData.previousContentLength || 0
                    );

                    // 🔥 重要：延迟重置恢复标志，不重新计算步骤数
                    setTimeout(() => {
                        setIsRestoringFromCache(false);

                        // 🔥 修复：从缓存恢复时，信任缓存中的步骤数，不重新计算
                        // 因为基于标题数量的计算不准确：一个长的思考过程可能包含多个小标题
                        // 但仍然只是一个思考步骤
                    }, 100);
                }
            }

            // 🔥 简化加载逻辑：优先恢复缓存，但确保缓存有效且为当前报告
            // 🔥 关键修复：添加报告ID匹配检查，防止恢复错误的缓存内容
            const shouldRestoreCache = !hasDisplayedContent && !isInitialLoadingRef.current && hasValidCache && !isReportChanged;

            if (shouldRestoreCache) {
                if (hasCachedContent) {
                    // 🔥 关键修复：双重验证缓存内容确实属于当前报告
                    const cacheData = reportCacheRef.current[currentReportKey];
                    const cacheHasRealContent =
                        (cacheData.activityContent && cacheData.activityContent.length > 50) ||
                        (cacheData.reportContent && cacheData.reportContent.length > 50);

                    // 🔥 三重验证：确保缓存键中包含当前报告ID
                    const cacheKeyMatchesCurrentReport = currentReportKey.includes(currentActiveReportId);

                    if (cacheHasRealContent && cacheKeyMatchesCurrentReport) {
                        // 🔥 关键修复：使用正确的cacheData变量而不是currentCache
                        const restoredActivityContent = processSpecialHeadings(cacheData.activityContent || '');
                        const restoredReportContent = processSpecialHeadings(cacheData.reportContent || '');

                        // 🔥 关键修复：设置恢复标志，防止内容变化检测误触发
                        setIsRestoringFromCache(true);

                        // 🔥 关键修复：先恢复步骤状态，再设置内容，避免触发错误的变化检测
                        if (cacheData.stepChangeCount !== undefined) {
                            setStepChangeCount(cacheData.stepChangeCount);
                        }
                        if (cacheData.contentChangeHistory !== undefined) {
                            setContentChangeHistory(cacheData.contentChangeHistory);
                        }
                        // 🔥 关键：设置正确的previousContentLength，防止内容变化检测误判
                        const currentContentLength = restoredActivityContent.length;
                        setPreviousContentLength(currentContentLength);

                        // 然后设置内容
                        setActivityMarkdownContent(restoredActivityContent);
                        setReportMarkdownContent(restoredReportContent);
                        setReportAvailable(!!cacheData.reportContent);
                        setContentLoaded(true);

                        // 🔥 新增：智能设置默认标签页 - 如果有报告内容或思考过程完成，默认显示报告标签
                        const maxSteps = 3;
                        const hasReportContent = !!(cacheData.reportContent && cacheData.reportContent.trim().length > 0);
                        const isThinkingComplete = (cacheData.stepChangeCount || 0) >= maxSteps;
                        const shouldShowReportSkeleton = isThinkingComplete && !hasReportContent;

                        if ((hasReportContent || shouldShowReportSkeleton) && !hasAutoSwitched && !userHasSwitched) {
                            setActiveTab('report');
                            setHasAutoSwitched(true);
                        }

                        // 🔥 重要：延迟重置恢复标志，确保内容变化检测不会误触发
                        setTimeout(() => {
                            setIsRestoringFromCache(false);
                        }, 100);
                    } else {
                        setActivityMarkdownContent('');
                        setReportMarkdownContent('');
                        setReportAvailable(false);
                        setContentLoaded(true);

                        // 🔥 新增：重置步骤状态
                        setStepChangeCount(0);
                        setContentChangeHistory([]);
                        setPreviousContentLength(0);
                    }

                    // 🔥 修复：如果是历史报告且有报告内容，触发完成事件确保按钮状态正确
                    if (data?.isFinal && cacheData.reportContent) {
                        // 🎯 完成的报告默认展示报告标签页
                        if (!hasAutoSwitched && !userHasSwitched) {
                            setActiveTab('report');
                            setHasAutoSwitched(true);
                        }

                        setTimeout(() => {
                            const reportCompleteEvent = new CustomEvent('report-complete-from-history', {
                                detail: {
                                    threadId,
                                    reportId: uniqueReportId || reportId,
                                    uniqueReportId: uniqueReportId || reportId,
                                    title: title || '研究报告',
                                    isFinal: true,
                                    status: 'success'
                                }
                            });
                            window.dispatchEvent(reportCompleteEvent);
                        }, 50); // 稍微延迟确保状态设置完成
                    }
                } else if (visibleReportId && !isActiveReportGenerating) {
                    // 🔥 修复：标记即将开始加载，避免其他useEffect重复调用
                    isInitialLoadingRef.current = true;
                    loadReportFromHistory().finally(() => {
                        isInitialLoadingRef.current = false; // 加载完成后重置标记
                    });
                }
            } else if (!hasValidCache && !hasDisplayedContent) {
                // 🔥 修复：如果缓存无效且没有显示内容，清空显示状态
                setActivityMarkdownContent('');
                setReportMarkdownContent('');
                setContentLoaded(true); // 🔥 关键修复：设为true避免频繁切换标签
                setReportAvailable(false);
                // 🔥 修复：不强制重置标签页，保持用户的选择
                // setActiveTab('activities'); // 删除强制重置，尊重用户选择
            } else if (isActiveReportGenerating) {
                setContentLoaded(true); // 标记为已加载，避免重复尝试
            }
        } else {
            // 侧边栏关闭时，停止本地轮询但保持内容缓存
            resetPolling();
        }
    }, [visible, data?.isFinal, reportId, uniqueReportId, threadId, getCurrentReportCache, getCurrentReportKey, loadReportFromHistory]);

    // 监听标记报告完成事件
    useEffect(() => {
        const handleMarkReportCompleted = (event) => {
            const { reportId: eventReportId, uniqueReportId: eventUniqueReportId, reason } = event.detail;
            const currentReportId = uniqueReportId || reportId;
            const eventEffectiveReportId = eventUniqueReportId || eventReportId;

            // 如果事件对应当前显示的报告，立即标记为已完成
            if (eventEffectiveReportId === currentReportId && visible) {
                // 标记所有相关ID为已完成
                const idsToMark = [eventReportId, eventUniqueReportId, reportId, uniqueReportId].filter(Boolean);
                idsToMark.forEach((id) => {
                    completedReportsRef.current.add(id);
                });
            }
        };

        window.addEventListener('mark-report-completed', handleMarkReportCompleted);

        return () => {
            window.removeEventListener('mark-report-completed', handleMarkReportCompleted);
        };
    }, [reportId, uniqueReportId, visible]);

    // 关闭侧边栏
    const handleClose = () => {
        if (onClose) {
            onClose();
        }
    };

    // 保存滚动位置的函数
    const saveScrollPosition = (containerType) => {
        const scrollRef = containerType === 'activities' ? activitiesScrollRef : reportScrollRef;
        if (scrollRef.current) {
            const scrollTop = scrollRef.current.scrollTop;
            const scrollHeight = scrollRef.current.scrollHeight;
            const clientHeight = scrollRef.current.clientHeight;

            // 保存滚动位置信息
            scrollRef.current.dataset.lastScrollTop = scrollTop;
            scrollRef.current.dataset.lastScrollHeight = scrollHeight;
            scrollRef.current.dataset.lastClientHeight = clientHeight;
        }
    };

    // 恢复滚动位置的函数
    const restoreScrollPosition = (containerType) => {
        const scrollRef = containerType === 'activities' ? activitiesScrollRef : reportScrollRef;
        if (scrollRef.current && scrollRef.current.dataset.lastScrollTop !== undefined) {
            const lastScrollTop = parseInt(scrollRef.current.dataset.lastScrollTop);
            const lastScrollHeight = parseInt(scrollRef.current.dataset.lastScrollHeight);
            const currentScrollHeight = scrollRef.current.scrollHeight;

            // 如果内容高度发生变化，调整滚动位置
            if (currentScrollHeight !== lastScrollHeight) {
                const heightDiff = currentScrollHeight - lastScrollHeight;
                // 如果内容增加了，并且之前滚动到底部附近，保持在底部
                if (heightDiff > 0) {
                    const wasNearBottom = lastScrollTop + scrollRef.current.clientHeight + 100 >= lastScrollHeight;
                    if (wasNearBottom) {
                        scrollRef.current.scrollTop = currentScrollHeight - scrollRef.current.clientHeight;
                    } else {
                        scrollRef.current.scrollTop = lastScrollTop + heightDiff;
                    }
                } else {
                    scrollRef.current.scrollTop = lastScrollTop;
                }
            } else {
                scrollRef.current.scrollTop = lastScrollTop;
            }
        }
    };

    // 启动轮询
    const startPolling = useCallback(async () => {
        const currentReportId = uniqueReportId || reportId;

        // 如果没有threadId但有reportId，尝试从本地存储获取threadId
        let effectiveThreadId = threadId;
        if (!effectiveThreadId && currentReportId) {
            try {
                const unifiedData = JSON.parse(localStorage.getItem('deerflow_reports_data') || '{"completed":{},"generating":{}}');
                const completedReports = unifiedData.completed || {};
                const generatingReports = unifiedData.generating || {};
                const reportData = completedReports[currentReportId] || generatingReports[currentReportId];
                if (reportData && reportData.threadId) {
                    effectiveThreadId = reportData.threadId;
                }
            } catch (error) {
                console.error('startPolling: 从本地存储获取threadId失败:', error);
            }
        }

        if (!effectiveThreadId) {
            console.warn('startPolling: 无法获取threadId，跳过轮询启动:', { threadId, currentReportId });
            return;
        }

        // 检查是否是当前显示的报告，避免为非当前报告启动轮询
        const isCurrentActiveReport =
            !activeReportCardId || currentReportId === activeReportCardId || reportId === activeReportCardId || uniqueReportId === activeReportCardId;

        if (!isCurrentActiveReport) {
            return;
        }

        // 🔥 增强的完成检查：检查多种完成标识，包括缓存中的状态
        const currentCache = getCurrentReportCache();
        const isReportCompleted =
            currentReportId &&
            (completedReportsRef.current.has(currentReportId) ||
                completedReportsRef.current.has(reportId) ||
                completedReportsRef.current.has(uniqueReportId) ||
                data?.isFinal ||
                (currentCache && currentCache.isFinal === true));

        if (isReportCompleted) {
            return;
        }

        // 额外保护：如果当前已有实质性内容且标记为final，也不启动轮询
        if (
            data?.isFinal &&
            ((activityMarkdownContent && activityMarkdownContent.length > 100) || (reportMarkdownContent && reportMarkdownContent.length > 100))
        ) {
            return;
        }

        // 检查是否已经有相同reportId的全局定时器在运行
        if (globalActiveReportId === currentReportId && globalPollingTimer) {
            isPollingRef.current = true;
            return;
        }

        isPollingRef.current = true;

        // 🔥 修复重复调用：立即执行一次，并检查是否已完成
        // 检查是否已经在初始加载中，避免重复调用
        if (!isInitialLoadingRef.current) {
            try {
                await loadReportFromHistory();
                // 如果在第一次调用后发现任务已完成，就不设置定时器了
                if (currentReportId && completedReportsRef.current.has(currentReportId)) {
                    isPollingRef.current = false;
                    return;
                }
            } catch (error) {
                console.error('首次轮询失败:', error);
            }
        }

        // 使用全局定时器管理
        setGlobalTimer(
            currentReportId,
            () => {
                // 在每次轮询前检查是否已完成
                if (currentReportId && completedReportsRef.current.has(currentReportId)) {
                    stopPolling();
                    return;
                }
                loadReportFromHistory();
            },
            5000
        );
    }, [uniqueReportId, reportId, threadId, data?.isFinal, loadReportFromHistory]);

    // 停止轮询
    const stopPolling = useCallback(() => {
        const currentReportId = uniqueReportId || reportId;

        // 如果当前是活跃的全局定时器，清理全局定时器
        if (globalActiveReportId === currentReportId) {
            clearGlobalTimer();
        }

        // 清理本地定时器引用（防护性措施）
        if (pollingTimerRef.current) {
            clearInterval(pollingTimerRef.current);
            pollingTimerRef.current = null;
        }

        isPollingRef.current = false;

        // 🔥 强化保护：确保已完成的报告被标记，防止重新启动轮询
        if (currentReportId) {
            completedReportsRef.current.add(currentReportId);
            if (reportId && reportId !== currentReportId) {
                completedReportsRef.current.add(reportId);
            }
            if (uniqueReportId && uniqueReportId !== currentReportId) {
                completedReportsRef.current.add(uniqueReportId);
            }
        }
    }, [uniqueReportId, reportId]);

    // 停止轮询但保持内容缓存（用于侧边栏关闭时）
    const resetPolling = () => {
        // 停止本地轮询
        if (pollingTimerRef.current) {
            clearInterval(pollingTimerRef.current);
            pollingTimerRef.current = null;
        }
        isPollingRef.current = false;

        // 重置防抖时间戳
        lastPollingTimeRef.current = 0;
    };

    // 完全重置状态和内容（用于切换到新报告时）
    const resetAll = useCallback(() => {
        resetPolling();
        setContentLoaded(false);
        setActivityMarkdownContent('');
        setReportMarkdownContent('');
        setReportAvailable(false);
        setTitle('研究报告'); // 重置标题
        // 🔥 修复：清理当前报告的缓存而不是全局缓存
        clearCurrentReportCache();
        setHasAutoSwitched(false);
        setUserHasSwitched(false);
        setIsGeneratingReport(false); // 🔥 新增：重置生成状态

        // 重置标签页到研究过程
        setActiveTab('activities');

        // 重置防抖相关状态
        lastPollingTimeRef.current = 0;
        lastUpdateTimeRef.current = 0;
    }, []);

    // 监听threadId或reportId变化，重新启动轮询
    // 注意：现在全局轮询由chat.jsx管理，这里只负责内容显示
    useEffect(() => {
        const currentReportId = uniqueReportId || reportId;
        const isReportCompleted = completedReportsRef.current.has(currentReportId);

        // 🔥 新增：检查报告是否已完成（isFinal状态）
        const currentCache = getCurrentReportCache();
        const isReportFinal = currentCache && currentCache.isFinal === true;

        // 🔥 关键修复：即使 threadId prop 暂时不可用，如果有正在生成的数据，也应该尝试启动轮询
        const hasGeneratingData = loadGeneratingStepsFromLocal(currentReportId);
        const shouldAttemptPolling = (threadId || hasGeneratingData) && visible; // 只在侧边栏可见时启动本地轮询

        if (shouldAttemptPolling) {
            // 只在侧边栏可见时启动本地轮询
            // 如果是新的报告ID，重置状态
            if (previousReportIdRef.current && previousReportIdRef.current !== currentReportId) {
                // 🔥 修复：如果新报告在本地存储中，不要立即重置，让本地存储加载逻辑先执行
                const hasLocalData = isReportInLocalStorage(currentReportId);

                if (!hasLocalData) {
                    // 立即清空显示的内容，避免显示旧报告内容
                    setActivityMarkdownContent('');
                    setReportMarkdownContent('');
                    setTitle('研究报告');
                    setContentLoaded(false);
                    setIsGeneratingReport(false); // 🔥 新增：重置生成状态
                    // 然后完全重置状态
                    resetAll();
                } else {
                    // 只清理缓存，但不重置UI状态，让本地存储加载逻辑处理
                    clearCurrentReportCache();

                    // 🎯 立即从本地存储获取标题，避免显示默认标题
                    const localReportData = loadCompletedReportFromLocal(currentReportId);
                    if (localReportData && localReportData.title && localReportData.title !== '研究报告') {
                        setTitle(localReportData.title);
                        // 设置previousReportIdRef，避免其他useEffect重置标题
                        previousReportIdRef.current = currentReportId;
                    }

                    // 🔥 修复重复调用：立即从本地存储加载数据，确保标题不被重置
                    setTimeout(() => {
                        if (!isInitialLoadingRef.current) {
                            loadReportFromHistory();
                        }
                    }, 50);
                }
            }
            previousReportIdRef.current = currentReportId;

            // 🔥 修复：如果报告已完成或已最终化，停止轮询
            if (isReportCompleted || isReportFinal) {
                if (isPollingRef.current) {
                    stopPolling();
                }

                // 🔥 新增：报告完成时清理正在生成的步骤状态
                clearGeneratingStepsFromLocal(currentReportId);
                setIsGeneratingReport(false);

                // 🔥 修复重复调用：如果是已完成的报告但没有内容，需要加载一次历史数据
                if (currentCache && !currentCache.activityContent && !currentCache.reportContent && !isInitialLoadingRef.current) {
                    loadReportFromHistory();
                }
                return;
            }

            // 🔥 新增：如果是正在生成的报告，设置生成状态
            if (!isReportCompleted && !isReportFinal) {
                setIsGeneratingReport(true);
            }

            // 🔥 关键修复：检查是否有正在生成的报告需要恢复轮询
            // 即使 threadId prop 暂时不可用，也应该尝试从本地存储获取并启动轮询
            const hasGeneratingData = loadGeneratingStepsFromLocal(currentReportId);
            const shouldStartPollingForGenerating = hasGeneratingData && !isReportCompleted && !isReportFinal;

            // 🔥 修复重复调用：检查是否已经在初始加载中，避免重复调用
            if (!isPollingRef.current && !isInitialLoadingRef.current) {
                // 如果有 threadId 或者有正在生成的数据，都应该启动轮询
                if (threadId || shouldStartPollingForGenerating) {
                    startPolling(); // 启动新的轮询
                }
            } else if (currentCache && !currentCache.activityContent && !currentCache.reportContent && !isInitialLoadingRef.current) {
                // 如果轮询已启动但还没有内容，立即尝试获取一次
                // 检查是否是当前显示的报告
                const isCurrentActiveReport =
                    !activeReportCardId ||
                    currentReportId === activeReportCardId ||
                    reportId === activeReportCardId ||
                    uniqueReportId === activeReportCardId;

                if (isCurrentActiveReport) {
                    loadReportFromHistory();
                } else {
                }
            }
        } else {
            if (isPollingRef.current) {
                stopPolling(); // 停止本地轮询，但全局轮询由chat.jsx管理
            }
        }

        return () => {
            // 组件卸载时，停止本地轮询和全局轮询
            const currentReportId = uniqueReportId || reportId;

            // 如果当前组件对应的是活跃的全局定时器，需要清理
            if (globalActiveReportId === currentReportId) {
                clearGlobalTimer();
            }

            resetPolling();
        };
    }, [threadId, visible, reportId, uniqueReportId, getCurrentReportCache, loadReportFromHistory, resetAll, startPolling, stopPolling]);

    // 处理传入的初始数据
    useEffect(() => {
        // 🛡️ 历史报告保护：在useEffect级别就阻止历史报告被覆盖
        const currentReportId = uniqueReportId || reportId;
        const isCurrentActiveReport =
            !activeReportCardId || currentReportId === activeReportCardId || reportId === activeReportCardId || uniqueReportId === activeReportCardId;

        // 🔥 修复：通过缓存检查是否已有内容，避免依赖状态导致死循环
        const currentCache = getCurrentReportCache();
        const hasExistingContent = currentCache && !!(currentCache.activityContent || currentCache.reportContent);

        // 如果当前显示的不是活跃报告，且已有内容，拒绝处理新数据
        if (!isCurrentActiveReport && hasExistingContent) {
            return;
        }

        // 检查当前报告ID是否与之前的不同，如果不同说明是新报告
        const isNewReport = previousReportIdRef.current && previousReportIdRef.current !== currentReportId;

        // 🔥 修复：如果是新报告，立即更新previousReportIdRef避免后续混淆
        if (isNewReport) {
            previousReportIdRef.current = currentReportId;

            // 🔥 关键修复：对于新报告，如果传入的数据为空或很少，不应该使用
            // 这可能是旧报告的残留数据
            const checkDataSize = (data) => {
                if (!data) return 0;
                if (Array.isArray(data)) {
                    return data.reduce((total, item) => total + (item.content?.length || 0), 0);
                }
                return data.length || 0;
            };

            const hasMinimalData = checkDataSize(activityData) < 100 && (!reportContent || reportContent.length < 100);
            if (hasMinimalData) {
                return;
            }
        }

        // 🔥 修复闪烁：精确的内容变化检测
        // 处理 activityData 可能是数组的情况
        const processActivityData = (data) => {
            if (!data) return '';
            if (Array.isArray(data)) {
                // 如果是数组，将每个元素的 content 合并
                return data.map((item) => item.content || '').join('\n\n');
            }
            return data;
        };

        const newActivityContent = processActivityData(activityData);
        const newReportContent = reportContent || '';
        const currentActivityLength = activityMarkdownContent?.length || 0;
        const currentReportLength = reportMarkdownContent?.length || 0;

        // 如果内容完全相同（长度相同且都非空），跳过处理（但允许初次加载）
        if (
            currentActivityLength === newActivityContent.length &&
            currentReportLength === newReportContent.length &&
            currentActivityLength > 0 &&
            contentLoaded
        ) {
            return;
        }

        // 检查activityData是否是占位符文本，如果是则不设置内容，让骨架屏显示
        const isPlaceholderText =
            activityData &&
            ((typeof activityData === 'string' &&
                (activityData.includes('正在准备研究数据') || activityData.includes('正在获取研究过程') || activityData.length < 50)) || // 如果内容太短，可能是占位符
                (Array.isArray(activityData) && activityData.length === 0));

        // 立即处理传入的数据，不等待轮询
        if ((activityData && !isPlaceholderText) || reportContent) {
            const newActivityData = activityData && !isPlaceholderText ? activityData : '';
            const newReportContent = reportContent || '';
            let newTitle = '研究报告';

            // 🔥 修复：从多个来源获取标题，优先级：data.title > 缓存标题 > 默认标题
            if (data && data.title && data.title !== '研究报告') {
                newTitle = data.title;
            } else if (currentCache && currentCache.title && currentCache.title !== '研究报告') {
                newTitle = currentCache.title;
            }

            // 使用优化的内容更新函数
            // 🔥 修复：正确处理传入数据中的isFinal状态
            const dataIsFinal = data && typeof data.isFinal === 'boolean' ? data.isFinal : null;
            updateContentSafely(newActivityData, newReportContent, newTitle, false, dataIsFinal);
        }

        // 标记初始加载完成
        if (isInitialLoad) {
            setIsInitialLoad(false);
        }
    }, [
        activityData,
        reportContent,
        data,
        threadId,
        reportId,
        uniqueReportId,
        activityMarkdownContent?.length,
        contentLoaded,
        getCurrentReportCache,
        isInitialLoad,
        reportMarkdownContent?.length,
        updateContentSafely
    ]);

    // 专门监听reportId和uniqueReportId的变化，确保立即响应切换
    useEffect(() => {
        const currentReportId = uniqueReportId || reportId;

        // 如果reportId发生变化，需要准备切换到新报告
        if (previousReportIdRef.current && previousReportIdRef.current !== currentReportId) {
            // 检查是否是切换到历史报告（从data?.isFinal或本地存储检查）
            const hasLocalData = isReportInLocalStorage(currentReportId);
            const isHistoricalReport = data?.isFinal || hasLocalData;

            if (isHistoricalReport) {
                // 检查是否已有缓存数据
                const currentCache = getCurrentReportCache();
                const hasCachedData = currentCache && !!(currentCache.activityContent || currentCache.reportContent);

                // 如果没有传入数据且没有缓存，则立即加载历史数据
                if (!activityData && !reportContent && !hasCachedData) {
                    setContentLoaded(false);

                    // 🔥 修复重复调用：立即尝试加载历史数据
                    setTimeout(() => {
                        if (!isInitialLoadingRef.current) {
                            isInitialLoadingRef.current = true;
                            loadReportFromHistory().finally(() => {
                                isInitialLoadingRef.current = false;
                            });
                        }
                    }, 100);
                } else {
                    // 🔥 修复：如果有缓存数据，也要触发完成事件，确保按钮状态正确
                    if (hasCachedData) {
                        // 🎯 检查缓存中是否有报告内容，如果有则默认展示报告标签页
                        const currentCache = getCurrentReportCache();
                        if (currentCache?.reportContent && !hasAutoSwitched && !userHasSwitched) {
                            setActiveTab('report');
                            setHasAutoSwitched(true);
                        }

                        const reportCompleteEvent = new CustomEvent('report-complete-from-history', {
                            detail: {
                                threadId,
                                reportId: uniqueReportId || reportId,
                                uniqueReportId: uniqueReportId || reportId,
                                title: title || '研究报告',
                                isFinal: true,
                                status: 'success'
                            }
                        });
                        window.dispatchEvent(reportCompleteEvent);
                    }
                }
            } else {
                // 🔥 修复：强制立即清空所有显示内容，防止显示旧报告内容
                setActivityMarkdownContent('');
                setReportMarkdownContent('');
                setReportAvailable(false);
                setTitle('研究报告');
                setContentLoaded(false);
                setActiveTab('activities'); // 重置到活动标签
                setHasAutoSwitched(false); // 重置自动切换标记
                setUserHasSwitched(false); // 重置用户手动切换标记

                // 🔥 修复：清理当前报告的缓存
                clearCurrentReportCache();
            }

            // 🔥 修复：更新previousReportIdRef以便下次检测变化
            previousReportIdRef.current = currentReportId;
        }
    }, [
        reportId,
        uniqueReportId,
        data?.isFinal,
        activityData,
        reportContent,
        activityMarkdownContent,
        clearCurrentReportCache,
        getCurrentReportCache,
        loadReportFromHistory,
        reportMarkdownContent,
        threadId,
        title
    ]);

    // 监听内容变化，控制加载状态
    useEffect(() => {
        // 如果没有任何内容，显示骨架屏
        if (!activityMarkdownContent && !reportMarkdownContent) {
            setContentLoaded(false);
            return;
        }

        // 有内容时，直接显示内容
        setContentLoaded(true);
    }, [activityMarkdownContent, reportMarkdownContent]);

    // 🔥 新增：监听标签页切换，确保报告内容正确显示
    useEffect(() => {
        if (activeTab === 'report' && !reportMarkdownContent) {
            const currentCache = getCurrentReportCache();
            if (currentCache?.reportContent) {
                setReportMarkdownContent(processSpecialHeadings(currentCache.reportContent));
                setReportAvailable(true);
                setContentLoaded(true);
            }
        }
    }, [activeTab, reportMarkdownContent, getCurrentReportCache]);

    // 监听普通消息完成事件
    useEffect(() => {
        const handleChatMessageComplete = async (event) => {
            const { messageId, type } = event.detail;

            if (type === 'normal_chat' && messageId) {
                try {
                    // 使用返回的content ID作为thread_id调用历史记录接口
                    const response = await handleDeerFlowHistory({ thread_id: messageId });

                    if (response && response.code === 200 && response.data) {
                        // 这里可以处理返回的数据，比如更新侧边栏内容
                        // 根据需要可以提取和处理数据
                    }
                } catch (error) {
                    console.error('获取普通消息历史记录失败:', error);
                }
            }
        };

        window.addEventListener('chatMessageComplete', handleChatMessageComplete);

        return () => {
            window.removeEventListener('chatMessageComplete', handleChatMessageComplete);
        };
    }, []);

    // 监听研究过程内容更新事件
    useEffect(() => {
        const handleActivityContentUpdate = (event) => {
            const { uniqueReportId: eventUniqueReportId, content, status } = event.detail;
            const currentReportId = uniqueReportId || reportId;

            // 🔥 关键修复：即使侧边栏关闭，也要更新本地存储
            // 但只有侧边栏可见时才更新UI
            if (eventUniqueReportId === currentReportId) {
                const shouldUpdateUI = visible;

                // 🔥 修复：允许研究过程的最终内容更新
                const currentCache = getCurrentReportCache();
                const isCurrentlyPolling = isPollingRef.current;
                const isFinalContent = status === 'success' || status === 'completed';

                // 只在以下情况阻止更新：
                // 1. 报告已在完成列表中 AND 不是最终内容更新
                // 2. 是历史报告 AND 不是最终内容更新 AND 当前不在轮询状态
                const shouldBlockUpdate =
                    (completedReportsRef.current.has(currentReportId) && !isFinalContent) ||
                    (currentCache?.activityContent?.length > 1000 && !isFinalContent && !isCurrentlyPolling);

                if (shouldBlockUpdate) {
                    return;
                }

                // 更新研究过程内容
                if (content) {
                    // flowData.js 发送的是单条 researcher 消息内容，需要累积
                    const existingContent = (currentCache && currentCache.activityContent) || '';

                    // 对于实时更新，检查是否是真正的新内容
                    // 如果现有内容已经很长（说明可能是历史报告），则更严格地检查
                    let isNewContent = false;

                    if (existingContent.length === 0) {
                        // 如果没有现有内容，这是第一条消息
                        isNewContent = true;
                    } else if (existingContent.length < 500) {
                        // 如果现有内容较短，可能是新报告的开始阶段
                        isNewContent = !existingContent.includes(content.trim());
                    } else {
                        // 如果现有内容已经很长，需要更严格的检查
                        // 检查内容是否是在现有内容的末尾追加
                        const trimmedContent = content.trim();
                        const trimmedExisting = existingContent.trim();

                        // 检查是否是追加内容（新内容不在现有内容中，且现有内容不包含新内容的开头）
                        isNewContent =
                            !trimmedExisting.includes(trimmedContent) &&
                            !trimmedContent.startsWith(trimmedExisting.substring(Math.max(0, trimmedExisting.length - 200)));
                    }

                    if (isNewContent) {
                        const newContent = existingContent ? existingContent + '\n\n' + content : content;

                        // 🔥 修复：更新当前报告的缓存，确认是否为最终版本（无论侧边栏是否可见）
                        const isFinalVersion = isFinalContent || data?.isFinal;
                        updateReportCache(newContent, (currentCache && currentCache.reportContent) || '', isFinalVersion);

                        // 🔥 关键修复：即使侧边栏关闭，也要保存到本地存储
                        const updatedCache = getCurrentReportCache();
                        if (updatedCache && currentReportId) {
                            saveGeneratingStepsToLocal(currentReportId, {
                                stepChangeCount: updatedCache.stepChangeCount || stepChangeCount,
                                contentChangeHistory: updatedCache.contentChangeHistory || contentChangeHistory,
                                previousContentLength: updatedCache.previousContentLength || previousContentLength,
                                activityContent: newContent,
                                reportContent: updatedCache.reportContent || ''
                            });
                        }

                        // 🔥 关键修复：只有侧边栏可见时才更新UI
                        if (shouldUpdateUI) {
                            setActivityMarkdownContent(processSpecialHeadings(newContent));
                            setContentLoaded(true);
                        }

                        // 🔥 如果是最终内容更新，标记为完成（无论侧边栏是否可见）
                        if (isFinalVersion) {
                            completedReportsRef.current.add(currentReportId);

                            // 🔥 新增：如果是最终内容，清理正在生成的步骤状态
                            clearGeneratingStepsFromLocal(currentReportId);
                            if (shouldUpdateUI) {
                                setIsGeneratingReport(false);
                            }
                        }
                    } else {
                    }
                }
            }
        };

        window.addEventListener('activity-content-update', handleActivityContentUpdate);

        return () => {
            window.removeEventListener('activity-content-update', handleActivityContentUpdate);
        };
    }, [reportId, uniqueReportId, visible, data?.isFinal, getCurrentReportCache, getCurrentReportKey, updateReportCache]);

    // 监听报告内容更新事件
    useEffect(() => {
        const handleReportContentUpdate = (event) => {
            const { uniqueReportId: eventUniqueReportId, content, status, title } = event.detail;
            const currentReportId = uniqueReportId || reportId;
            // 🔥 关键修复：即使侧边栏关闭，也要更新本地存储
            // 但只有侧边栏可见时才更新UI
            if (eventUniqueReportId === currentReportId) {
                const shouldUpdateUI = visible;

                // 🔥 修复：允许报告生成完成时的最终内容更新
                const currentCache = getCurrentReportCache();
                const isCurrentlyPolling = isPollingRef.current;
                const isFinalContent = status === 'success' || status === 'completed';

                // 只在以下情况阻止更新：
                // 1. 报告已在完成列表中 AND 不是最终内容更新
                // 2. 是历史报告 AND 不是最终内容更新 AND 当前不在轮询状态
                const shouldBlockUpdate =
                    (completedReportsRef.current.has(currentReportId) && !isFinalContent) ||
                    (currentCache?.reportContent?.length > 1000 && !isFinalContent && !isCurrentlyPolling);

                if (shouldBlockUpdate) {
                    return;
                }

                // 🔥 关键修复：处理标题更新事件（无论侧边栏是否可见都更新缓存）
                if (title && title !== '研究报告') {
                    // 如果有缓存，也更新缓存中的标题信息
                    if (currentCache) {
                        // 这里不直接修改缓存，让updateContentSafely处理
                        updateContentSafely(
                            currentCache.activityContent || '',
                            currentCache.reportContent || '',
                            title,
                            false, // 不是来自缓存
                            currentCache.isFinal
                        );
                    }

                    // 🔥 只有侧边栏可见时才更新UI
                    if (shouldUpdateUI) {
                        setTitle(title);
                        // 🔥 关键修复：如果缓存中有报告内容，确保reportAvailable为true
                        if (currentCache?.reportContent && currentCache.reportContent.trim().length > 0) {
                            setReportAvailable(true);
                            setContentLoaded(true);
                        }
                    }
                }

                // 🔥 新增：处理状态更新但没有内容的情况（如报告完成事件）
                if (isFinalContent && !content && currentCache?.reportContent) {
                    // 🔥 只有侧边栏可见时才更新UI
                    if (shouldUpdateUI) {
                        setReportAvailable(true);
                        setContentLoaded(true);

                        // 如果当前显示的内容为空但缓存中有内容，恢复显示
                        if (!reportMarkdownContent && currentCache.reportContent) {
                            setReportMarkdownContent(processSpecialHeadings(currentCache.reportContent));
                        }
                    }

                    // 🔥 新增：通知useChat.js更新loading状态（无论侧边栏是否可见）
                    if (threadId) {
                        window.dispatchEvent(
                            new CustomEvent('session-loading-update', {
                                detail: {
                                    sessionId: threadId,
                                    loading: false,
                                    answering: false,
                                    timestamp: Date.now()
                                }
                            })
                        );
                    }
                }

                // 更新报告内容
                if (content) {
                    // 🔥 修复：更新当前报告的缓存，确认是否为最终版本（无论侧边栏是否可见）
                    const isFinalVersion = isFinalContent || data?.isFinal;
                    updateReportCache(currentCache?.activityContent || '', content, isFinalVersion);

                    // 🔥 关键修复：即使侧边栏关闭，也要保存到本地存储
                    const updatedCache = getCurrentReportCache();
                    if (updatedCache && currentReportId) {
                        saveGeneratingStepsToLocal(currentReportId, {
                            stepChangeCount: updatedCache.stepChangeCount || stepChangeCount,
                            contentChangeHistory: updatedCache.contentChangeHistory || contentChangeHistory,
                            previousContentLength: updatedCache.previousContentLength || previousContentLength,
                            activityContent: updatedCache.activityContent || '',
                            reportContent: content
                        });
                    }

                    // 🔥 只有侧边栏可见时才更新UI
                    if (shouldUpdateUI) {
                        setReportMarkdownContent(processSpecialHeadings(content));
                        setReportAvailable(true);
                        setContentLoaded(true); // 确保标记内容已加载

                        // 自动切换到报告标签页（只切换一次，且仅在用户未手动选择其他标签时）
                        if (!hasAutoSwitched && !userHasSwitched && content.length > 0 && activeTab === 'activities') {
                            setActiveTab('report');
                            setHasAutoSwitched(true);
                        }
                    }

                    // 🔥 关键修复：如果是最终内容，标记报告完成并停止轮询（无论侧边栏是否可见）
                    if (isFinalVersion) {
                        completedReportsRef.current.add(currentReportId);
                        if (shouldUpdateUI) {
                            isPollingRef.current = false;
                        }

                        // 🔥 新增：通知useChat.js更新loading状态
                        if (threadId) {
                            window.dispatchEvent(
                                new CustomEvent('session-loading-update', {
                                    detail: {
                                        sessionId: threadId,
                                        loading: false,
                                        answering: false,
                                        timestamp: Date.now()
                                    }
                                })
                            );
                        }

                        // 🔥 新增：保存完成的报告到本地存储
                        const finalActivityContent = currentCache?.activityContent || '';
                        const finalReportContent = content;
                        const finalTitle = title || extractTitleFromContent(content) || '研究报告';

                        if (finalActivityContent || finalReportContent) {
                            const reportDataToSave = {
                                reportId: currentReportId,
                                uniqueReportId: currentReportId,
                                threadId,
                                title: finalTitle,
                                activityContent: finalActivityContent,
                                reportContent: finalReportContent,
                                timestamp: Date.now()
                            };

                            saveCompletedReportToLocal(reportDataToSave);
                        }

                        // 🔥 新增：报告完成时清理正在生成的步骤状态
                        clearGeneratingStepsFromLocal(currentReportId);
                        setIsGeneratingReport(false);

                        // 触发报告完成事件
                        setTimeout(() => {
                            // 🎯 从本地存储获取真实标题
                            const localReportData = loadCompletedReportFromLocal(currentReportId);
                            const realTitle =
                                localReportData && localReportData.title && localReportData.title !== '研究报告' ? localReportData.title : title; // 使用当前标题作为后备

                            const reportCompleteEvent = new CustomEvent('report-state-change', {
                                detail: {
                                    reportId: currentReportId,
                                    uniqueReportId: currentReportId,
                                    status: 'success',
                                    title: realTitle // 📝 传递真实标题
                                }
                            });
                            window.dispatchEvent(reportCompleteEvent);
                        }, 100); // 稍微延迟确保UI更新完成
                    }
                }

                // 更新状态
                if (status === 'success' || status === 'completed') {
                }
            }
        };

        window.addEventListener('report-content-update', handleReportContentUpdate);

        return () => {
            window.removeEventListener('report-content-update', handleReportContentUpdate);
        };
    }, [
        reportId,
        uniqueReportId,
        hasAutoSwitched,
        visible,
        data?.isFinal,
        getCurrentReportCache,
        getCurrentReportKey,
        updateReportCache,
        updateContentSafely
    ]);

    // 监听报告完成事件（从历史记录API确认的完成）
    useEffect(() => {
        const handleReportCompleteFromHistory = (event) => {
            const { reportId: eventReportId, uniqueReportId: eventUniqueReportId, status } = event.detail;
            const currentReportId = uniqueReportId || reportId;

            // 检查是否是当前报告
            if (eventReportId === currentReportId || eventUniqueReportId === currentReportId) {
                // 发送状态更新事件到报告卡片
                // 🎯 从本地存储获取真实标题
                const localReportData = loadCompletedReportFromLocal(currentReportId);
                const realTitle = localReportData && localReportData.title && localReportData.title !== '研究报告' ? localReportData.title : title; // 使用当前标题作为后备

                const stateUpdateEvent = new CustomEvent('report-state-change', {
                    detail: {
                        reportId: currentReportId,
                        uniqueReportId: currentReportId,
                        status: status || 'success',
                        userStopped: false,
                        title: realTitle // 📝 传递真实标题
                    }
                });
                window.dispatchEvent(stateUpdateEvent);
            }
        };

        window.addEventListener('report-complete-from-history', handleReportCompleteFromHistory);

        return () => {
            window.removeEventListener('report-complete-from-history', handleReportCompleteFromHistory);
        };
    }, [reportId, uniqueReportId]);

    // 🎯 监听会话切换事件，根据会话ID查找并更新报告标题
    useEffect(() => {
        const handleSessionSwitched = (event) => {
            const { sessionId, skillId } = event.detail;

            // 只处理DeerFlow会话
            if (skillId !== '1955175276228874242') {
                return;
            }

            // 查找这个会话对应的完成报告
            const localReportData = loadCompletedReportFromLocal(sessionId);

            if (localReportData && localReportData.title && localReportData.title !== '研究报告') {
                // 如果当前显示的报告ID与切换的会话ID匹配，更新标题
                const currentActiveReportId = uniqueReportId || reportId;
                if (currentActiveReportId === sessionId) {
                    setTitle(localReportData.title);

                    // 同时更新缓存中的标题
                    const currentCache = getCurrentReportCache();
                    if (currentCache) {
                        updateReportCache(
                            currentCache.activityContent || '',
                            currentCache.reportContent || '',
                            currentCache.isFinal || false,
                            localReportData.title
                        );
                    }

                    // 设置previousReportIdRef，避免其他useEffect重置标题
                    previousReportIdRef.current = currentActiveReportId;
                }
            }
        };

        window.addEventListener('session-switched', handleSessionSwitched);

        return () => {
            window.removeEventListener('session-switched', handleSessionSwitched);
        };
    }, [reportId, uniqueReportId, loadCompletedReportFromLocal, getCurrentReportCache, updateReportCache]);

    // 🔥 移除：不再需要ID映射更新事件监听器，因为我们现在直接使用真实ID

    // 🔥 新增：监听内容长度变化，追踪思考过程步骤
    useEffect(() => {
        // 🔥 关键修复：如果正在从缓存恢复，跳过变化检测
        if (isRestoringFromCache) {
            return;
        }

        const currentLength = activityMarkdownContent?.length || 0;

        // 🔥 重置逻辑：当内容变为空时，重置所有追踪状态
        if (currentLength === 0 && previousContentLength > 0) {
            setStepChangeCount(0);
            setContentChangeHistory([]);
            setPreviousContentLength(0);

            // 🔥 新增：重置缓存
            updateStepStateCache(0, [], 0);
            return;
        }

        if (currentLength > 0 && currentLength !== previousContentLength) {
            const currentTime = Date.now();
            const currentReportId = uniqueReportId || reportId;

            // 记录这次变化
            const newChange = {
                timestamp: currentTime,
                length: currentLength,
                lengthDiff: currentLength - previousContentLength
            };

            // 🔥 关键逻辑：判断是否是显著的内容变化（新的思考步骤）
            if (previousContentLength === 0) {
                // 从0变为有内容，这是第一次变化（第一个思考过程开始）
                const newStepCount = 1;
                const newHistory = [newChange];
                setStepChangeCount(newStepCount);
                setContentChangeHistory(newHistory);

                // 🔥 修复：立即保存步骤状态到本地存储
                if (currentReportId) {
                    saveGeneratingStepsToLocal(currentReportId, {
                        stepChangeCount: newStepCount,
                        contentChangeHistory: newHistory,
                        previousContentLength: currentLength,
                        activityContent: activityMarkdownContent || '',
                        reportContent: reportMarkdownContent || ''
                    });
                }

                // 🔥 新增：更新步骤状态缓存
                updateStepStateCache(newStepCount, newHistory, currentLength);
            } else {
                // 检查是否是显著的内容增量（新的思考步骤）
                const lengthIncrease = currentLength - previousContentLength;
                const isSignificantChange = lengthIncrease > 200; // 至少增加200个字符才算新步骤

                // 检查时间间隔（避免频繁的小更新被误认为新步骤）
                const timeSinceLastChange =
                    contentChangeHistory.length > 0 ? currentTime - contentChangeHistory[contentChangeHistory.length - 1].timestamp : 0;
                const hasTimeGap = timeSinceLastChange > 1000; // 至少间隔1秒

                if (isSignificantChange && hasTimeGap && stepChangeCount < 4) {
                    // 🔥 修改：改为4步
                    const newStepCount = stepChangeCount + 1;
                    const newHistory = [...contentChangeHistory, newChange];
                    setStepChangeCount(newStepCount);
                    setContentChangeHistory(newHistory);

                    // 🔥 修复：立即保存步骤状态到本地存储
                    if (currentReportId) {
                        saveGeneratingStepsToLocal(currentReportId, {
                            stepChangeCount: newStepCount,
                            contentChangeHistory: newHistory,
                            previousContentLength: currentLength,
                            activityContent: activityMarkdownContent || '',
                            reportContent: reportMarkdownContent || ''
                        });
                    }

                    // 🔥 新增：更新步骤状态缓存
                    updateStepStateCache(newStepCount, newHistory, currentLength);
                } else {
                    // 更新历史记录但不增加步骤数（可能是同一步骤的继续更新）
                    const newHistory = [...contentChangeHistory, newChange];
                    setContentChangeHistory(newHistory);

                    // 🔥 修复：也要保存历史记录到本地存储
                    if (currentReportId) {
                        saveGeneratingStepsToLocal(currentReportId, {
                            stepChangeCount,
                            contentChangeHistory: newHistory,
                            previousContentLength: currentLength,
                            activityContent: activityMarkdownContent || '',
                            reportContent: reportMarkdownContent || ''
                        });
                    }

                    // 🔥 新增：更新历史记录缓存
                    updateStepStateCache(stepChangeCount, newHistory, currentLength);
                }
            }

            setPreviousContentLength(currentLength);
        }
    }, [
        activityMarkdownContent,
        previousContentLength,
        stepChangeCount,
        contentChangeHistory,
        updateStepStateCache,
        isRestoringFromCache,
        uniqueReportId,
        reportId,
        reportMarkdownContent
    ]);

    // 🔥 重新设计：基于内容变化追踪的步骤解析函数
    const parseActivitySteps = useCallback(
        (content, activityDataArray = null) => {
            // 🔥 优先使用我们的变化追踪结果
            if (stepChangeCount > 0) {
                return Array.from({ length: stepChangeCount }, (_, i) => ({
                    step: i + 1,
                    isChangeTracked: true
                }));
            }

            // 🔥 对于历史记录或其他情况，使用原有逻辑但更加保守
            if (activityDataArray && Array.isArray(activityDataArray)) {
                // 过滤掉 reporter 角色的消息（通常是最终报告）
                const researchSteps = activityDataArray.filter(
                    (item) =>
                        item.role === 'assistant' &&
                        (item.name === 'researcher' || item.name === 'coder') &&
                        item.content &&
                        item.content.length > 100
                );

                let stepCount = researchSteps.length;

                // 更保守的历史记录步骤识别
                if (stepCount === 1 && researchSteps[0].content.length > 2000) {
                    const content = researchSteps[0].content;
                    // 只有明确的步骤分隔符才认为是多步骤
                    const hasMultipleSteps = [/第[一二三四五六七八九十1-9][步阶段]/g, /步骤[一二三四五六七八九十1-9]/g].some((pattern) => {
                        const matches = content.match(pattern);
                        return matches && matches.length >= 2;
                    });

                    if (hasMultipleSteps) {
                        stepCount = Math.min(stepCount + 1, 4); // 🔥 修改：改为4步
                    }
                }

                stepCount = Math.min(stepCount, 4); // 🔥 修改：改为4步
                return Array.from({ length: stepCount }, (_, i) => ({ step: i + 1 }));
            }

            if (!content || typeof content !== 'string') return [];

            // 🔥 后备方案：基于内容长度的非常保守的估算
            const contentLength = content.length;
            let stepCount = 0;

            if (contentLength < 100) {
                stepCount = 0;
            } else if (contentLength < 1000) {
                stepCount = 1;
            } else if (contentLength < 2500) {
                stepCount = 2;
            } else if (contentLength < 4000) {
                stepCount = 3;
            } else {
                stepCount = 4; // 🔥 修改：增加第4步
            }

            return Array.from({ length: stepCount }, (_, i) => ({ step: i + 1 }));
        },
        [stepChangeCount]
    );

    // 🔥 重新设计：基于步骤变化追踪的自动切换逻辑
    useEffect(() => {
        if (!hasAutoSwitched && !userHasSwitched && activeTab === 'activities' && activityMarkdownContent) {
            const maxSteps = 3; // 🔥 修改：改为3步

            // 🔥 关键修改：使用我们的步骤变化计数
            const currentStepCount = stepChangeCount;

            // 🔥 思考过程完成的条件：
            // 1. 达到3个步骤变化 OR
            // 2. 有报告内容生成
            const currentCache = getCurrentReportCache();
            const hasReportContent = reportMarkdownContent || currentCache?.reportContent;
            const isThinkingComplete = currentStepCount >= maxSteps || hasReportContent;

            // 当思考过程完成时，减少延迟并立即切换到报告标签
            if (isThinkingComplete) {
                const timer = setTimeout(() => {
                    setActiveTab('report');
                    setHasAutoSwitched(true);
                }, 800); // 🔥 减少延迟到800ms，让用户更快看到报告

                return () => clearTimeout(timer);
            }
        }
    }, [hasAutoSwitched, userHasSwitched, activeTab, activityMarkdownContent, stepChangeCount, reportMarkdownContent, getCurrentReportCache]);

    // 🔥 当threadId变化时，重新同步下载状态（使用ref避免重绘）
    useEffect(() => {
        if (threadId) {
            try {
                const key = getDownloadStateKey('pdf');
                const stored = localStorage.getItem(key);
                const isStored = stored === 'true';
                isDownloadingPdfRef.current = isStored;
            } catch (error) {
                console.warn('Failed to sync download state:', error);
                isDownloadingPdfRef.current = false;
            }
        } else {
            // threadId为空时重置状态
            isDownloadingPdfRef.current = false;
        }
    }, [threadId]);

    // 骨架屏组件（单个步骤）
    const StepSkeletonLoader = () => (
        <div className="step-skeleton-loader" style={{ '--theme-color': currentThemeColor }}>
            <div className="skeleton-step-header">
                <div className="skeleton-step-title"></div>
            </div>
            <div className="skeleton-step-content">
                <div className="skeleton-paragraph">
                    <div className="skeleton-line skeleton-line-long"></div>
                    <div className="skeleton-line skeleton-line-medium"></div>
                    <div className="skeleton-line skeleton-line-short"></div>
                </div>
                <div className="skeleton-paragraph">
                    <div className="skeleton-line skeleton-line-medium"></div>
                    <div className="skeleton-line skeleton-line-long"></div>
                </div>
            </div>
        </div>
    );

    // 报告骨架屏组件
    const ReportSkeletonLoader = () => (
        <div className="report-skeleton-loader" style={{ '--theme-color': currentThemeColor }}>
            {/* 报告标题骨架 */}
            <div className="skeleton-report-title">
                <div className="skeleton-line skeleton-line-title"></div>
            </div>

            {/* 报告内容骨架 */}
            <div className="skeleton-report-content">
                <div className="skeleton-section">
                    <div className="skeleton-section-title"></div>
                    <div className="skeleton-paragraph">
                        <div className="skeleton-line skeleton-line-long"></div>
                        <div className="skeleton-line skeleton-line-medium"></div>
                        <div className="skeleton-line skeleton-line-short"></div>
                    </div>
                </div>
                <div className="skeleton-section">
                    <div className="skeleton-section-title"></div>
                    <div className="skeleton-paragraph">
                        <div className="skeleton-line skeleton-line-medium"></div>
                        <div className="skeleton-line skeleton-line-long"></div>
                        <div className="skeleton-line skeleton-line-medium"></div>
                    </div>
                </div>
                <div className="skeleton-section">
                    <div className="skeleton-section-title"></div>
                    <div className="skeleton-paragraph">
                        <div className="skeleton-line skeleton-line-long"></div>
                        <div className="skeleton-line skeleton-line-short"></div>
                    </div>
                </div>
            </div>
        </div>
    );

    // 🔥 优化：使用防抖处理PDF下载点击
    const handlePdfDownload = useCallback(
        async (e) => {
            e.preventDefault();

            // 检查是否正在下载
            if (getIsDownloadingPdf()) {
                const now = Date.now();
                const timeSinceLastWarning = now - lastWarningTimeRef.current;

                if (timeSinceLastWarning > 3000) {
                    message.warning('PDF正在下载中，请等待当前下载完成');
                    lastWarningTimeRef.current = now;
                }
                return;
            }

            try {
                // 使用ref避免状态更新导致的重绘
                setIsDownloadingPdf(true);

                const cache = getCurrentReportCache();
                if (!cache?.reportContent) {
                    message.error('没有可导出的内容');
                    return;
                }

                let contentToExport = cache.reportContent;
                if (typeof contentToExport !== 'string') {
                    console.warn('reportContent不是字符串，尝试转换:', typeof contentToExport, contentToExport);
                    if (contentToExport && typeof contentToExport === 'object') {
                        if (contentToExport.text) {
                            contentToExport = contentToExport.text;
                        } else if (contentToExport.content) {
                            contentToExport = contentToExport.content;
                        } else if (contentToExport.markdown) {
                            contentToExport = contentToExport.markdown;
                        } else {
                            contentToExport = JSON.stringify(contentToExport, null, 2);
                        }
                    } else {
                        contentToExport = String(contentToExport || '');
                    }
                }

                // 🔥 新增：在PDF内容末尾右对齐添加AI生成内容提示
                const contentWithDisclaimer =
                    contentToExport +
                    '\n\n---\n\n<div style="text-align: right; font-style: italic; color: #666; margin-top: 20px;">(注：文档包含AI生成内容，请注意鉴别)</div>';

                const fileName = generateDownloadFileName(title, 'pdf');
                await downloadPdf(contentWithDisclaimer, fileName.replace('.pdf', ''));
            } catch (error) {
                console.error('PDF导出失败:', error);
                message.error('PDF导出失败，请重试');
            } finally {
                setIsDownloadingPdf(false);
            }
        },
        [getCurrentReportCache, title, getIsDownloadingPdf, setIsDownloadingPdf]
    );

    // 组件渲染
    return (
        <div className="report-panel">
            {/* 侧边栏头部 */}
            <div className="report-header">
                <Tabs
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    defaultActiveKey="activities"
                    items={[
                        {
                            key: 'report',
                            label: '报告',
                            disabled: (() => {
                                // 🔥 修复禁用条件：考虑报告内容、缓存内容和骨架屏状态
                                const cache = getCurrentReportCache();
                                const hasReportContent = reportMarkdownContent && reportMarkdownContent.trim().length > 0;
                                const hasCachedReportContent = cache?.reportContent && cache.reportContent.trim().length > 0;

                                // 检查是否应该显示报告骨架屏（思考过程完成但报告未生成）
                                const maxSteps = 3; // 🔥 修改：改为3步
                                const steps = parseActivitySteps(activityMarkdownContent, activityData);
                                const isThinkingComplete = steps.length >= maxSteps;
                                const shouldShowReportSkeleton = isThinkingComplete && !hasReportContent && !hasCachedReportContent;

                                // 如果有报告内容、缓存内容或应该显示骨架屏，则启用报告标签
                                return !hasReportContent && !hasCachedReportContent && !shouldShowReportSkeleton;
                            })()
                        },
                        { key: 'activities', label: '思考过程' }
                    ]}
                />
                <Space>
                    {/* 只在报告标签页且有报告内容时显示导出按钮 */}
                    {activeTab === 'report' && reportAvailable && (
                        <div
                            className="export-dropdown"
                            onMouseEnter={() => {
                                setExportBtnHover(true);
                                setShowExportMenu(true);
                            }}
                            onMouseLeave={() => {
                                setExportBtnHover(false);
                                setShowExportMenu(false);
                            }}
                        >
                            <Button type="text" className="export-button">
                                导出
                                <img src={currentDownloadIcon} alt="download" className={`export-icon ${exportBtnHover ? 'active' : ''}`} />
                            </Button>
                            {showExportMenu && (
                                <div className={`export-menu ${showExportMenu ? 'show' : ''}`}>
                                    <div
                                        className={`export-item ${isDownloadingMd ? 'downloading' : ''}`}
                                        onClick={async () => {
                                            // 检查是否正在下载
                                            if (isDownloadingMd) {
                                                // 🔥 新增：防重复提示机制 - 只在5秒内显示一次警告
                                                const now = Date.now();
                                                const timeSinceLastWarning = now - lastMdWarningTimeRef.current;

                                                if (timeSinceLastWarning > 3000) {
                                                    // 3秒内只显示一次
                                                    message.warning('MD3秒才能下载一次');
                                                    lastMdWarningTimeRef.current = now;
                                                }
                                                return;
                                            }

                                            try {
                                                // 设置下载状态
                                                setIsDownloadingMd(true);

                                                const cache = getCurrentReportCache();
                                                if (cache?.reportContent) {
                                                    // 🔥 新增：在MD内容末尾右对齐添加AI生成内容提示
                                                    const contentWithDisclaimer =
                                                        cache.reportContent + '\n\n---\n\n(注：文档包含AI生成内容，请注意鉴别)';
                                                    const fileName = generateDownloadFileName(title, 'md');
                                                    downloadFile(contentWithDisclaimer, fileName, 'text/markdown');
                                                }
                                            } catch (error) {
                                                console.error('MD导出失败:', error);
                                                message.error('MD导出失败，请重试');
                                            } finally {
                                                // 3秒后清除下载状态，防止重复下载
                                                setTimeout(() => {
                                                    setIsDownloadingMd(false);
                                                }, 3000);
                                            }
                                        }}
                                    >
                                        .md
                                    </div>
                                    <div
                                        className={`export-item docx-enhanced ${isDownloadingDocx ? 'downloading' : ''}`}
                                        onClick={async (e) => {
                                            e.preventDefault();

                                            // 检查是否正在下载
                                            if (isDownloadingDocx) {
                                                // 🔥 新增：防重复提示机制 - 只在5秒内显示一次警告
                                                const now = Date.now();
                                                const timeSinceLastWarning = now - lastDocxWarningTimeRef.current;

                                                if (timeSinceLastWarning > 3000) {
                                                    // 3秒内只显示一次
                                                    message.warning('DOCX3秒才能下载一次');
                                                    lastDocxWarningTimeRef.current = now;
                                                }
                                                return;
                                            }

                                            try {
                                                // 设置下载状态
                                                setIsDownloadingDocx(true);

                                                // 直接导出报告内容
                                                const cache = getCurrentReportCache();
                                                if (!cache?.reportContent) {
                                                    message.error('没有可导出的内容');
                                                    return;
                                                }

                                                // 确保内容是字符串格式
                                                let contentToExport = cache.reportContent;
                                                if (typeof contentToExport !== 'string') {
                                                    if (contentToExport && typeof contentToExport === 'object') {
                                                        // 如果是对象，尝试提取可能的文本内容
                                                        if (contentToExport.text) {
                                                            contentToExport = contentToExport.text;
                                                        } else if (contentToExport.content) {
                                                            contentToExport = contentToExport.content;
                                                        } else if (contentToExport.markdown) {
                                                            contentToExport = contentToExport.markdown;
                                                        } else {
                                                            contentToExport = JSON.stringify(contentToExport, null, 2);
                                                        }
                                                    } else {
                                                        contentToExport = String(contentToExport || '');
                                                    }
                                                }

                                                // 🔥 新增：在DOCX内容末尾添加AI生成内容提示（纯文本格式）
                                                const contentWithDisclaimer = contentToExport + '\n\n---\n\n(注：文档包含AI生成内容，请注意鉴别)';

                                                // 使用增强版DOCX生成器
                                                const fileName = generateDownloadFileName(title, 'docx');
                                                await downloadDocxWithStyle(
                                                    contentWithDisclaimer,
                                                    fileName.replace('.docx', ''),
                                                    'minimal' // 使用简洁风格
                                                );
                                            } catch (error) {
                                                console.error('DOCX导出失败:', error);
                                                message.error('DOCX导出失败，请重试');
                                            } finally {
                                                // 3秒后清除下载状态，防止重复下载
                                                setTimeout(() => {
                                                    setIsDownloadingDocx(false);
                                                }, 3000);
                                            }
                                        }}
                                    >
                                        .docx
                                        <span className="enhanced-badge" title="增强版DOCX生成器 - 支持多种样式">
                                            ⚡
                                        </span>
                                    </div>
                                    <div
                                        className={`export-item pdf-enhanced ${getIsDownloadingPdf() ? 'downloading' : ''}`}
                                        onClick={handlePdfDownload}
                                    >
                                        {'.pdf'}
                                        {!getIsDownloadingPdf() && (
                                            <span className="enhanced-badge" title="增强版PDF生成器 - 支持多种样式">
                                                ⚡
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <Button type="text" icon={<CloseOutlined />} onClick={handleClose} style={{ color: '#666666' }} />
                </Space>
            </div>

            {/* 报告内容 */}
            <div className="report-content">
                {(() => {
                    // 🔥 修复：更精确的骨架屏显示判断
                    const hasAnyContent = activityMarkdownContent || reportMarkdownContent;
                    const shouldShowSkeleton = !contentLoaded || !hasAnyContent;

                    if (shouldShowSkeleton) {
                        return (
                            <div className="markdown-content-container skeleton-container loaded" style={{ '--theme-color': currentThemeColor }}>
                                <ReportSkeletonLoader />
                            </div>
                        );
                    }

                    return (
                        <>
                            {activeTab === 'activities' && (
                                <div
                                    className="markdown-content-container activities loaded"
                                    ref={activitiesScrollRef}
                                    style={{ '--theme-color': currentThemeColor }}
                                >
                                    {(() => {
                                        const maxSteps = 3; // 🔥 修改：最大思考步骤数改为3
                                        const currentStepCount = stepChangeCount; // 🔥 使用我们的步骤变化计数

                                        // 🔥 修复：检查是否应该显示骨架屏
                                        const shouldShowInitialSkeleton = !activityMarkdownContent || activityMarkdownContent.length === 0;

                                        if (shouldShowInitialSkeleton) {
                                            // 🔥 长度为0：显示第一个思考过程的骨架屏
                                            return (
                                                <div className="activities-progressive-loading">
                                                    <StepSkeletonLoader />
                                                </div>
                                            );
                                        }

                                        // 🔥 核心逻辑：检查思考过程是否真正完成
                                        const currentCache = getCurrentReportCache();
                                        const hasReportContent = reportMarkdownContent || currentCache?.reportContent;

                                        // 🔥 思考过程完成条件：
                                        // 1. 步骤数达到最大值（4次内容变化）OR
                                        // 2. 有报告内容生成
                                        const isThinkingComplete = currentStepCount >= maxSteps || hasReportContent;
                                        // 有内容时，渲染已有的思考过程内容 + 条件性显示下一个步骤的骨架屏
                                        return (
                                            <div className="activities-progressive-loading">
                                                {/* 渲染已有的思考过程内容 */}
                                                <div className="completed-activities">
                                                    <Markdown content={activityMarkdownContent} />
                                                </div>

                                                {/* 🔥 关键逻辑：
                          - 第1次变化后：显示第2个思考过程的骨架屏
                          - 第2次变化后：显示第3个思考过程的骨架屏  
                          - 第3次变化后：显示第4个思考过程的骨架屏
                          - 第4次变化后：隐藏骨架屏，准备切换到报告标签
                      */}
                                                {!isThinkingComplete && (
                                                    <div className="next-step-skeleton">
                                                        <StepSkeletonLoader />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}
                            {activeTab === 'report' && (
                                <div
                                    className="markdown-content-container report loaded"
                                    ref={reportScrollRef}
                                    style={{ '--theme-color': currentThemeColor }}
                                >
                                    {(() => {
                                        // 🔥 关键修复：优先显示reportMarkdownContent，如果为空则从缓存获取
                                        const currentCache = getCurrentReportCache();
                                        const displayContent =
                                            reportMarkdownContent ||
                                            (currentCache?.reportContent ? processSpecialHeadings(currentCache.reportContent) : null);

                                        if (displayContent) {
                                            // 🔥 如果从缓存获取到内容但reportMarkdownContent为空，同步更新状态
                                            if (!reportMarkdownContent && currentCache?.reportContent) {
                                                setTimeout(() => {
                                                    setReportMarkdownContent(processSpecialHeadings(currentCache.reportContent));
                                                    setReportAvailable(true);
                                                }, 0);
                                            }
                                            return <Markdown content={displayContent} />;
                                        } else {
                                            // 检查思考过程是否完成（3个步骤都完成了）
                                            const maxSteps = 3; // 🔥 修改：改为3步
                                            const steps = parseActivitySteps(activityMarkdownContent, activityData);
                                            const isThinkingComplete = steps.length >= maxSteps;

                                            if (isThinkingComplete) {
                                                // 思考过程完成但报告还未生成，显示报告骨架屏
                                                return <ReportSkeletonLoader />;
                                            } else {
                                                // 思考过程还未完成，显示等待状态
                                                return (
                                                    <div className="empty-content" style={{ '--theme-color': currentThemeColor }}>
                                                        <div className="empty-icon">📄</div>
                                                        <p>等待思考过程完成...</p>
                                                        <div className="loading-dots">
                                                            <span></span>
                                                            <span></span>
                                                            <span></span>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        }
                                    })()}
                                </div>
                            )}
                        </>
                    );
                })()}
            </div>
        </div>
    );
};
export default SimpleReportSidebar;
